import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Lock, Paperclip, Play, X, Sparkles, Check, Archive, ExternalLink, Folder as FolderIcon, PanelLeftClose, PanelLeftOpen, MoreHorizontal } from 'lucide-react'
import {
  artifacts,
  createWall,
  removeDocument,
  matters as mattersApi,
  refileArtifact,
  refileSiblings,
  type RefileSibling,
  results as resultsApi,
  runPipelineBulk,
  runPipelineForMatter,
  shortId,
  workProduct,
  type Artifact,
  type ArtifactDetail,
  type Matter,
  type RefileResult,
  type WorkProductType,
  type ChangeCarry,
} from '../api'
import { useAuth } from '../auth'
import { isPlayheadTenant } from '../playhead'
import { useProductName } from '../productName'
import { Glyph } from '../glyphs'
import BodyPreview from '../components/BodyPreview'
import MarkupModal from '../components/MarkupModal'
import ChangeCarryModal from '../components/ChangeCarryModal'
import ContractReplyModal from '../components/ContractReplyModal'
import ApprovalsConsole, { type ApprovalsData } from '../components/ApprovalsConsole'
import PrioritiesChat from '../components/PrioritiesChat'
import AgentChatModal from '../components/AgentChatModal'
import AgreementsGutter from '../components/AgreementsGutter'
import { getAgreementQueueGlyph } from '../uiConfig'
import PullDialog, { PULL_TYPES } from '../components/PullDialog'
import { sidebarUserInteracted } from '../sidebarSession'
import { gmailReplyUrl } from '../gmailLink'
import { ArtifactRecipients } from '../components/ArtifactRecipients'
import { MultiIssueBlock } from '../components/MultiIssueBlock'
import { copyArtifactToClipboard } from '../components/artifactCopy'
import { ChatBubble, TriangleBubble } from '../components/ChatPanel'
import ClassificationCard from '../components/ClassificationCard'
import MatterTimelinePane from '../components/MatterTimelinePane'
import {
  useScrollMemory,
  markScrollPendingRestore,
  clearScrollPendingRestore,
  rememberLastArtifact,
  recallLastArtifact,
  decideRememberAction,
  decideRecallAction,
} from '../components/scrollMemory'
import FolderSidebar, { MatterRowCompact, FolderPickerModal } from '../components/FolderSidebar'
import { traceSearch, traceWallCreate, traceRefile } from '../tracking/searchTrace'

// "Last synced" — the real workspace-sync heartbeat: finished_at of the most
// recent SUCCESSFUL sync run (tenant-scoped), NOT the newest email's timestamp.
// Eastern time, no seconds, no tz label; polls every 60s so it tracks the ~2-min
// sync. Ken 2026-08-17 (was corpus_edge, which read stale when no new mail
// had arrived — showed the newest email's date, not when we last polled).
function LastSynced() {
  // KEN/playhead copy + type treatment (Ken 2026-10-23). Derived here (not
  // threaded as a prop) since this is a top-level component outside Workspace.
  const auth = useAuth()
  const playhead = auth.status === 'authenticated' && isPlayheadTenant(auth.user.client?.code)
  const { data } = useQuery({
    queryKey: ['corpus-edge'],
    queryFn: () => mattersApi.corpusEdge(),
    staleTime: 5_000,
    // Poll fast while the pipeline is settling so "Sorting…" drains to "Up to
    // date" promptly; slow when idle so the ambient line is cheap (Ken 2026-08-23).
    refetchInterval: (q) => ((q.state.data?.in_flight ?? 0) > 0 ? 3_000 : 15_000),
  })
  const ts = data?.last_sync_at
  const inFlight = data?.in_flight ?? 0
  const label = ts
    ? new Date(ts).toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : '—'
  // "Synced <when we last checked Google> · <whether the sidebar has caught up>".
  // Two distinct facts: the sync time is NOT the moment mail becomes sidebar
  // activity — the L1–L9 pipeline classifies it seconds-to-minutes later. The
  // "sorting" state describes work in flight, NOT a promise of new matters (some
  // items resolve to personal/digest and add nothing); it just means the sidebar
  // isn't necessarily complete yet. Copy per Ken 2026-08-23.
  // KEN: when idle, show ONLY "Synced <when>" — no "up to date" suffix (Ken
  // 2026-10-23). The in-flight "sorting…" state is kept (it's informative).
  // MOVU keeps the "· sidebar up to date" idle suffix.
  const state = inFlight > 0
    ? 'sorting new email and docs'
    : (playhead ? null : 'sidebar up to date')
  // KEN: match the unified pane-1 control-strip type — 11.5px regular sans
  // (me-brand, NOT mono), muted. MOVU unchanged (no className).
  return (
    <span className={playhead ? 'me-brand normal-case font-normal tabular-nums text-[11.5px] me-text-mute' : undefined}>
      Synced {label}{state ? ` · ${state}` : ''}
    </span>
  )
}

// KEN pane-1 head (SPEC rev 2026-10-25): the SYNCED stamp — the FIRST line in the
// pane, above FIND. A stamp, not a setting. Label SYNCED (ui-mono 10px/.11em/#9B958A,
// 100px column) + value = last-sync time only (no date).
function SyncedStamp() {
  const { data } = useQuery({
    queryKey: ['corpus-edge'],
    queryFn: () => mattersApi.corpusEdge(),
    staleTime: 5_000,
  })
  const ts = data?.last_sync_at
  const time = ts
    ? new Date(ts).toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })
    : '—'
  return (
    // UXT-6 6A-1: the SYNCED row's first baseline sits on the pane-2 matter-name
    // baseline (the band datum). +9px pushes it down to that shared baseline;
    // line-height:1 makes the baseline deterministic. (Harness-measured; holds at
    // every reading size because all three panes share the same zoom.)
    <div className="inline-flex items-baseline" style={{ gap: 16, marginTop: 9, lineHeight: 1 }}>
      <span className="me-chrome-label">SYNCED</span>
      <span className="me-chrome-value is-timestamp">{time}</span>
    </div>
  )
}

// URGENCY_LABELS retained for any future inline matter-row chip; for now
// the matter detail header uses a numeric urgency badge (5/5 etc.).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const URGENCY_LABELS = ['', 'low', 'med-low', 'med', 'med-high', 'urgent']
void URGENCY_LABELS

export default function Workspace() {
  const auth = useAuth()
  // "What's New" triage chat — studio-only for now (Ken 2026-10-12: a button only he sees).
  const [whatsNewOpen, setWhatsNewOpen] = useState(false)
  const canSeeWhatsNew = auth.status === 'authenticated' && auth.user.has_studio_access
  // Per-user opt-in (shadow_ui flag → /me) for the pane-1 "Proposed Agreements"
  // gutter. OFF by default; flipped on for Heather/Maria. (Ken 2026-10-13)
  const canSeeAgreementsGutter = auth.status === 'authenticated' && !!auth.user.cic_agreements_gutter
  const isAdmin = auth.status === 'authenticated' && (auth.user.is_staff || auth.user.is_superuser)
  // KEN/playhead: pane dividers become hover-only resize-handle columns; MOVU
  // keeps the existing 1px gap divider (Ken 2026-10-22).
  const playhead = auth.status === 'authenticated' && isPlayheadTenant(auth.user.client?.code)
  // Tenant code for namespacing client-side state (folder open/close in
  // localStorage). Falls back to a sentinel for the cross-tenant superuser
  // case so we still get isolated state.
  const authedClientCode = (
    auth.status === 'authenticated' && auth.user.client?.code
  ) || '__unscoped__'
  // Folder-bar collapse (Maria 2026-10-18 — she reads better with the folder
  // bar out of the way; collapsing it hands pane-2/pane-3 the full 400px).
  // Per-workstation, persisted to localStorage like the folder open/close state.
  const sidebarCollapseKey = `me:folderbar-collapsed:${authedClientCode}`
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(sidebarCollapseKey) === '1' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem(sidebarCollapseKey, sidebarCollapsed ? '1' : '0') } catch { /* ignore */ }
  }, [sidebarCollapseKey, sidebarCollapsed])
  // Reading size (Maria 2026-10-19) — per-user profile setting (/me reading_size).
  // Scales ONLY panes 2 & 3 via the .me-reading zoom rule; pane 1 stays dense.
  const readingSize = (auth.status === 'authenticated' ? auth.user.reading_size : 'normal') || 'normal'
  const readingScale = readingSize === 'larger' ? 1.3 : readingSize === 'large' ? 1.15 : 1
  // limit=500 — sidebar shows all matters in one scrollable list (no pager
  // pattern). With ~80 matters today and the AI generating ~5-10/day during
  // bulk runs, 500 is plenty of headroom. If we ever blow past 500 we'll
  // add real pagination here, but for now scrolling is simpler.
  const matters = useQuery({
    // Params live in the queryKey alongside the resource name so two
    // useQuery sites with different params can't corrupt each other's
    // cache slot (we hit this when ArtifactViewer fetched the default
    // 50-page list under bare `['matters']` while Workspace fetched
    // 500). invalidateQueries({queryKey: ['matters']}) still matches
    // both because react-query does prefix matching.
    // limit high enough to load EVERY matter — the sidebar tree + its "N
    // matters" counter must be complete, else both under-count vs Stats and
    // the oldest matters silently drop off the tree (Ken 2026-08-10: sidebar
    // 420 vs Stats 456 was the 500-cap truncating ~55 of 555 matters).
    queryKey: ['matters', { limit: 5000 }],
    queryFn: () => mattersApi.list({ limit: 5000 }),
    // Background poll keeps the sidebar + matter header fresh while
    // the user is sitting in Workspace. Pipeline writes update
    // Matter.summary / .stage / .waiting_on / .urgency from celery,
    // and other users may be editing concurrently. 20s is the budget
    // — ETag short-circuits unchanged polls (304, ~100 bytes) so the
    // server cost is small even with multiple active sessions.
    refetchInterval: 20_000,
  })
  // URL-backed selection state so back/forward and the artifact viewer's
  // back button restore exactly what was selected. ?matter=<id|inbox>
  // and ?artifact=<id>; defaults to inbox / no artifact.
  const [searchParams, setSearchParams] = useSearchParams()
  // Preserve the pane selection across leaving Main and returning (Ken
  // 2026-08-11): persist the URL selection params, and if we remount on a bare
  // /workspace (e.g. after visiting Stats and clicking Main), restore them.
  useEffect(() => {
    const s = searchParams.toString()
    if (s) { try { sessionStorage.setItem('me.workspaceSel', s) } catch { /* ignore */ } }
  }, [searchParams])
  useEffect(() => {
    if (searchParams.toString() === '') {
      try {
        const saved = sessionStorage.getItem('me.workspaceSel')
        if (saved) setSearchParams(new URLSearchParams(saved), { replace: true })
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const selectedMatterId: number | 'inbox' | null = (() => {
    const v = searchParams.get('matter')
    // No ?matter → clean home (empty panes), NOT the In-Process view. In
    // Process is still reachable directly via the ?matter=inbox URL (the
    // sidebar shortcut icon was removed 2026-08-17).
    if (v === null) return null
    if (v === 'inbox') return 'inbox'
    if (v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  })()
  // UXT-9 9A: the pane-2/pane-3 rule has a HOLE across the title band and resumes
  // level with the top of the view-controls row (Chronology/Phases/Docs). The title
  // band height is dynamic (1-/2-line name × reading size), so measure the controls
  // row's top relative to the grid rather than hard-coding a height. Re-measures on
  // matter switch, reading size, resize, and any pane-2-head size change (wrap/zoom).
  const gridRef = useRef<HTMLDivElement>(null)
  const [dividerRuleTop, setDividerRuleTop] = useState(0)
  // UXT-10 10A: the hole-bottom datum is the TOP of the first metadata line
  // beneath the pane-3 stem (the sender line, [data-pane3-sender]) — not the
  // pane-2 view-controls row. "Pane-3 wins." When pane-3 has no sender
  // (nothing selected, or a document that carries no addressing), we hold the
  // last good value so the rule never jumps to the top; on a cold start with
  // nothing ever measured, we fall back to the pane-2 controls-row top so the
  // hole still exists.
  const lastRuleTopRef = useRef(0)
  useEffect(() => {
    const measure = () => {
      const grid = gridRef.current
      if (!grid) return
      const gridTop = grid.getBoundingClientRect().top
      const sender = document.querySelector('[data-pane3-sender]') as HTMLElement | null
      let top: number
      if (sender) {
        top = Math.max(0, Math.round(sender.getBoundingClientRect().top - gridTop))
        lastRuleTopRef.current = top
      } else if (lastRuleTopRef.current > 0) {
        top = lastRuleTopRef.current
      } else {
        const controls = document.querySelector('[data-pane2-controls]') as HTMLElement | null
        top = controls ? Math.max(0, Math.round(controls.getBoundingClientRect().top - gridTop)) : 0
      }
      setDividerRuleTop((p) => (p !== top ? top : p))
    }
    measure()
    const raf = requestAnimationFrame(measure)
    const head = document.querySelector('[data-pane2-head]') as HTMLElement | null
    const col = document.querySelector('[data-pane3-col]') as HTMLElement | null
    const ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(() => measure()) : null
    if (ro && head) ro.observe(head)
    if (ro && col) ro.observe(col)
    window.addEventListener('resize', measure)
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); window.removeEventListener('resize', measure) }
  }, [playhead, selectedMatterId, readingSize, searchParams.get('artifact')])
  const selectedArtifactId: number | null = (() => {
    const v = searchParams.get('artifact')
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  })()
  const setSelectedMatterId = (id: number | 'inbox' | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (id === null || id === 'inbox') {
        if (id === null) next.set('matter', '')
        else next.set('matter', 'inbox')
      } else {
        next.set('matter', String(id))
      }
      // Clear artifact when changing matters
      next.delete('artifact')
      return next
    }, { replace: false })
  }
  const setSelectedArtifactId = (id: number | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (id == null) next.delete('artifact')
      else next.set('artifact', String(id))
      // Clear markup mode whenever the selected artifact changes —
      // markup view always exits when the selection switches; entry
      // is exclusively via the badge click which sets it back.
      next.delete('markup')
      return next
    }, { replace: false })
  }
  // Pane-3 markup view toggle. `markup=1` in the URL means pane-3 is
  // showing the markup table instead of BodyPreview for the currently
  // selected artifact. Cleared by selecting a different artifact, by
  // re-clicking the same attachment card (returns to content view),
  // or by switching matter / clearing the artifact.
  const markupMode: boolean = searchParams.get('markup') === '1'
  // Change-carry modal (Ken 2026-11-02): self-contained, fed by the artifact's
  // change_carry (no fetch). Opened from the pane-2 "N of M carried" chip.
  const [changeCarry, setChangeCarry] = useState<{ data: ChangeCarry; filename: string } | null>(null)
  // Re-click of the active attachment card while in markup mode →
  // exit markup. Re-click while NOT in markup → no-op (you already
  // see the content view). Badge click → enter markup (handled by
  // setMarkupMode(true) below in the timeline pane wiring).
  const setMarkupMode = (on: boolean) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (on) next.set('markup', '1')
      else next.delete('markup')
      return next
    }, { replace: false })
  }
  // Folder-context for the currently-selected matter. Captures which
  // folder occurrence was clicked (a matter can appear in multiple
  // folders); back/reload restores the same folder path. NULL =
  // matter clicked from Unfiled or no folder context.
  const selectedFolderId: number | null = (() => {
    const v = searchParams.get('folder')
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  })()
  // Combined setter: clicking a matter in folder X sets matter + folder
  // atomically. Artifact is cleared only when switching to a DIFFERENT
  // matter — re-clicking the same matter from another folder context
  // (a matter filed in 2+ folders) preserves the artifact selection,
  // since the user is just navigating folder paths, not opening a
  // new matter.
  const setSelectedMatterInFolder = (matterId: number, folderId: number | null) => {
    // Clicking a sidebar matter puts arrow-key focus in the sidebar (click-only
    // path — not called programmatically, so it's safe to set the zone here).
    navZoneRef.current = 'sidebar'
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const prevMatter = prev.get('matter')
      const sameMatter = prevMatter === String(matterId)
      next.set('matter', String(matterId))
      if (folderId == null) next.delete('folder')
      else next.set('folder', String(folderId))
      if (!sameMatter) next.delete('artifact')
      return next
    }, { replace: false })
  }
  // Scroll memory — column 1 (matters list) restores ONLY when we're
  // returning from an ArtifactViewer round-trip (pending flag), so direct
  // visits to /workspace land at the top to surface new arrivals. Column
  // 2 (per-matter artifact list) restores per-matter always, so switching
  // between matters in-session feels like resuming context.
  const mattersListRef = useRef<HTMLDivElement>(null)
  // Which pane the arrow keys drive, tracked independently of DOM focus so a
  // React re-render (which can drop focus) doesn't send Up/Down to the wrong
  // pane (Ken 2026-08-10: nav was "jumpy, sometimes doesn't work"). Default
  // 'timeline' — a fresh session's arrows drive pane-2.
  const navZoneRef = useRef<'sidebar' | 'timeline'>('timeline')
  const inboxListRef = useRef<HTMLElement>(null)
  // Sidebar scroll restore needs to wait for the matters list to load
  // before the saved scrollTop has any meaningful target. On a refresh,
  // useScrollMemory now also activates restore (via isPageReload) — but
  // attempting to set scrollTop on an empty <aside> is a no-op, so we
  // gate on matters.data being present.
  useScrollMemory(mattersListRef, 'me.scroll.matters', 'pending', !!matters.data)
  useScrollMemory(
    inboxListRef,
    `me.scroll.artifacts.${selectedMatterId === 'inbox' ? 'inbox' : selectedMatterId ?? 'none'}`,
    'always',
  )
  // Clear the pending flag once we've had a chance to consume it. Without
  // this a stray refresh later would still trigger restore.
  useEffect(() => {
    const t = window.setTimeout(() => clearScrollPendingRestore(), 100)
    return () => window.clearTimeout(t)
  }, [])

  // Persist last-selected artifact per (matter|inbox). Decision logic
  // (the part that's burned us with race conditions twice) lives in
  // decideRememberAction in scrollMemory.ts and is unit-tested. This
  // effect just wires inputs in and dispatches the resulting action.
  const prevMatterIdRef = useRef<string | number | null>(null)
  useEffect(() => {
    const action = decideRememberAction({
      prevMatterId: prevMatterIdRef.current,
      currentMatterId: selectedMatterId,
      currentArtifactId: selectedArtifactId,
    })
    prevMatterIdRef.current = selectedMatterId
    if (selectedMatterId === null) return
    const matterScope = selectedMatterId === 'inbox' ? 'inbox' : selectedMatterId
    if (action === 'store' && selectedArtifactId) {
      rememberLastArtifact(matterScope, selectedArtifactId)
    } else if (action === 'clear') {
      rememberLastArtifact(matterScope, null)
    }
  }, [selectedMatterId, selectedArtifactId])

  const [matterFilter, setMatterFilter] = useState('')
  // Ken 2026-07-08 — ref for the "Search matters…" input in the pane-1
  // sidebar. `/` key shortcut focuses it (see keydown handler below).
  const matterSearchRef = useRef<HTMLInputElement>(null)

  // Detects an artifact CODE in the search box: EMAL/DOCU/ATCH + a sequence,
  // tolerant of whatever separator the user actually types — the hyphen the UI
  // shows (`EMAL-011476`), the SPACE the QA box trained them on (`EMAL 011476`),
  // an underscore/colon, or none — with or without the `MOVU-GOOG-` prefix.
  // Stateless (no /g flag), safe to reuse across render + handlers. Used to (a)
  // arm Enter-to-jump and (b) show the "↵ open" hint. Bare numbers are
  // deliberately NOT a trigger (they'd hijack normal matter-name typing).
  const ARTIFACT_CODE_RE = /(?:^|[\s\-_:])(?:EMAL|DOCU|ATCH)[\s\-_:]*\d+$/i

  // Unadvertised power lookup (Ken 2026-08-17): typing an artifact CODE into the
  // matter search box + Enter jumps straight to that artifact, regardless of
  // which matter it's filed under. Backend `/artifacts/?search=` resolves the
  // code → client_seq (ignored=any so auto-ignored rows are still findable); we
  // match that seq exactly, then open the artifact in its home matter (or the
  // inbox when it isn't filed). Kept off the placeholder on purpose.
  const jumpToArtifactCode = async (raw: string) => {
    // Pull the type + sequence out regardless of separator / leading zeros, then
    // normalize to the canonical zero-padded hyphen form the backend matches
    // tightest on (a bare number would also match content mentioning it).
    const m = raw.trim().match(/(EMAL|DOCU|ATCH)[\s\-_:]*0*(\d+)$/i)
    if (!m) return
    const seq = parseInt(m[2], 10)
    if (!Number.isFinite(seq)) return
    const canonical = `${m[1].toUpperCase()}-${String(seq).padStart(6, '0')}`
    let rows
    try {
      rows = await artifacts.list({ search: canonical, limit: 5, ignored: 'any' })
    } catch {
      return
    }
    const hit = rows.find((r) => r.client_seq === seq) ?? rows[0]
    if (!hit) return  // no match → leave the (now-empty) matter list as implicit "not found"
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const mid = hit.matter_ids && hit.matter_ids.length ? hit.matter_ids[0] : null
      next.set('matter', mid != null ? String(mid) : 'inbox')
      next.set('artifact', String(hit.id))
      return next
    }, { replace: false })
    setMatterFilter('')
  }

  // Ken 2026-07-08 — `/` focuses the sidebar's matter search. Guarded
  // so the key still types normally when focus is inside any text
  // input (chat, EditableText, folder rename, etc.) — matches the same
  // input-guard pattern the arrow-nav handlers use. Selects existing
  // text so a stale filter is replaced on the next keystroke rather
  // than appended to.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' && e.key !== 'x' && e.key !== 'X') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const tgt = e.target as HTMLElement | null
      const tag = tgt?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (tgt && tgt.isContentEditable) return
      const el = matterSearchRef.current
      if (!el) return
      e.preventDefault()
      if (e.key === '/') {
        el.focus()
        el.select()
      } else {
        // 'x' — clear the search bar (mirror the Clear button): drop the filter
        // + selection, refocus the box (Ken 2026-08-11).
        setMatterFilter('')
        setSelectedMatterId(null)
        el.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  // Sort of the flat list (drives the Archived list order + the matters
  // handed to FolderSidebar, which re-sorts the tree by its own toggle).
  // No longer user-cycled from the sidebar — the FOLDERS sort toggle owns
  // sorting now; this just keeps a stable persisted order for the flat list.
  const [matterSort] = useState<'recent_desc' | 'alpha' | 'code'>(
    () => {
      const s = (typeof localStorage !== 'undefined' && localStorage.getItem('me_matter_sort')) || ''
      // Legacy values (recent_asc) fall through to recent_desc; the
      // "oldest first" mode was retired.
      return s === 'alpha' || s === 'code' ? s : 'recent_desc'
    },
  )
  // Snapshot of matter ids the user has already "seen" (i.e. already
  // had a chance to notice). Initial value = whatever matters existed
  // when the workspace first loaded. We add to it whenever a brand-new
  // matter's row has rendered+flashed once, so a subsequent collapse/
  // re-expand of its folder (which unmounts/remounts the row) doesn't
  // re-fire the flash. "Flash exactly once per (page-load, matter-id)."
  const [seenAtLoad, setSeenAtLoad] = useState<Set<number> | null>(null)
  useEffect(() => {
    if (seenAtLoad === null && matters.data) {
      setSeenAtLoad(new Set(matters.data.map((m) => m.id)))
    }
  }, [matters.data, seenAtLoad])
  const markSeen = (id: number) => {
    setSeenAtLoad((prev) => {
      if (prev === null || prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  // Bulk-select state, scoped to the current artifact list
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [lastRunMsg, setLastRunMsg] = useState<string | null>(null)

  const runBulk = useMutation({
    mutationFn: (ids: number[]) => runPipelineBulk(ids),
    onSuccess: (data) => {
      setLastRunMsg(`Enqueued ${data.enqueued}${data.rejected ? `, ${data.rejected} rejected (cross-tenant)` : ''}.`)
      setSelectedIds(new Set())
    },
  })
  const runForMatter = useMutation({
    mutationFn: (matterId: number) => runPipelineForMatter(matterId),
    onSuccess: (data) => setLastRunMsg(`Enqueued ${data.enqueued} for matter #${data.matter_id}.`),
  })

  // Matter view: per-matter list. Bumped to limit=500 so a matter
  // with many artifacts isn't silently truncated to the DRF page size
  // of 50. (Per-matter pagination can come if a matter ever exceeds
  // 500 artifacts; today they don't.)
  const matterArtifacts = useQuery({
    queryKey: ['artifacts', 'matter', selectedMatterId],
    queryFn: () =>
      typeof selectedMatterId === 'number'
        ? artifacts.list({ matter: selectedMatterId, limit: 500 })
        : Promise.resolve([]),
    enabled: typeof selectedMatterId === 'number',
    // Background poll: pipeline writes ArtifactMatter rows from celery
    // (AI files an artifact into this matter) while the user sits here.
    // Only fires for the OPEN matter — react-query polls mounted+enabled
    // queries only, and `enabled` is gated on the selected id.
    refetchInterval: 30_000,
    // No keepPreviousData here — when switching matters, we want the
    // list to clear and the spinner to take over. Showing the previous
    // matter's content (even dimmed) was potentially confusing now that
    // we have a clear loading indicator. Hover-prefetch on matter rows
    // means the data is usually already warm by the time the user
    // clicks, so the empty state is brief in practice.
  })


  // Inbox view: paginated, prev/next/+10/-10 (matches the artifacts
  // browser pattern). Server-side filter has_matter=false +
  // ignored=false (default). Order: newest source_date first; secondary
  // by id ASC so an email and its attachments cluster (attachments
  // inherited the email's source_date at import + via the backfill
  // SQL, and their ids are higher than the parent's since emails
  // import before attachments).
  const INBOX_PAGE_SIZE = 50
  const [inboxOffset, setInboxOffset] = useState(0)
  // Reset offset when switching INTO inbox view.
  useEffect(() => {
    if (selectedMatterId === 'inbox') setInboxOffset(0)
  }, [selectedMatterId])

  const inboxQuery = useQuery({
    queryKey: ['inbox-page', inboxOffset],
    queryFn: () =>
      artifacts.page({
        has_matter: 'false',
        limit: INBOX_PAGE_SIZE,
        offset: inboxOffset,
        ordering: '-source_date,id',
      }),
    enabled: selectedMatterId === 'inbox',
    placeholderData: keepPreviousData,
    // Background poll: import + auto-classification writes new
    // unfiled artifacts while the user sits in the inbox view.
    // Same gating story as matterArtifacts above.
    refetchInterval: 30_000,
  })
  const inboxTotal = inboxQuery.data?.count ?? 0
  const inboxItems: Artifact[] = inboxQuery.data?.results ?? []
  const inboxLastPageOffset = inboxTotal === 0 ? 0 : (Math.ceil(inboxTotal / INBOX_PAGE_SIZE) - 1) * INBOX_PAGE_SIZE
  const inboxJump = (deltaPages: number) => {
    const target = Math.max(0, Math.min(inboxLastPageOffset, inboxOffset + deltaPages * INBOX_PAGE_SIZE))
    setInboxOffset(target)
  }

  // Unified list the rest of the file consumes. `inboxOnly` keeps its
  // historical name for diff-clarity; it's just "the visible
  // artifacts list" regardless of matter / inbox.
  const inboxOnly: Artifact[] = useMemo(() => {
    if (typeof selectedMatterId === 'number') return matterArtifacts.data ?? []
    if (selectedMatterId === 'inbox') return inboxItems
    return []
  }, [selectedMatterId, matterArtifacts.data, inboxItems])

  // Ken 2026-07-05: keyboard nav in any chronology view. ↑ and ↓
  // advance selection to the previous / next artifact card in
  // whatever order the timeline is CURRENTLY rendering. Iterating
  // `inboxOnly` was wrong — that's the raw data array, not the
  // visual order (spine sorts by narrative group, feed nests
  // attachments and linked-docs under their parent email, etc.),
  // so children got skipped and up/down felt jumbled.
  //
  // Fix: query the DOM for `[data-artifact-id]` — that returns
  // elements in document tree order which matches visual order
  // across every timeline mode. Rows carry the attribute on their
  // outer div; linked-doc pseudo-attachment rows carry the doc's
  // artifact_id so navigation walks through them like any other
  // child card.
  //
  // Also move browser focus to the newly-selected row (rows are
  // `tabIndex={0}`). Prior handler updated selection but not focus,
  // so the row the user last clicked kept its `:focus` outline
  // (Ken 2026-07-05 report: "blue outline left on the first
  // attachment"). Focus-follows-selection releases that ring.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key !== 'ArrowDown' && e.key !== 'ArrowUp' &&
        e.key !== 'ArrowLeft' && e.key !== 'ArrowRight'
      ) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const tgt = e.target as HTMLElement | null
      const tag = tgt?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (tgt && (tgt as HTMLElement).isContentEditable) return

      // Keyboard browsing → clear the parked-mouse echoes in the sidebar so
      // nothing lingers while navigating by key (Ken 2026-08-10): the dwell
      // tools (via the event) AND the hover grey (via the me-kbd-nav class,
      // which neutralises row :hover until the mouse moves again).
      window.dispatchEvent(new CustomEvent('me-row-reveal-clear'))
      document.documentElement.classList.add('me-kbd-nav')

      // Cross-pane + intra-pane arrow navigation (rewritten Ken 2026-08-10 for
      // robustness). Everything anchors on SELECTION (React state), not DOM
      // focus, so a re-render that drops focus can't misroute the keys. A
      // nav-zone ref remembers which pane the arrows drive when focus is lost.
      const focusedMatter = tgt?.closest('[data-matter-id]') as HTMLElement | null
      const focusedCard = tgt?.closest('[data-artifact-id]') as HTMLElement | null
      const matterEls = Array.from(document.querySelectorAll<HTMLElement>('[data-matter-id]'))
      const cardEls = Array.from(document.querySelectorAll<HTMLElement>('[data-artifact-id]'))
      const centerY = (el: HTMLElement) => { const r = el.getBoundingClientRect(); return r.top + r.height / 2 }
      const nearestByY = (list: HTMLElement[], src: HTMLElement) => {
        const cy = centerY(src)
        let best = list[0], bestDist = Infinity
        for (const el of list) { const d = Math.abs(centerY(el) - cy); if (d < bestDist) { bestDist = d; best = el } }
        return best
      }
      const go = (el: HTMLElement) => {
        if (typeof el.focus === 'function') el.focus({ preventScroll: false })
        el.scrollIntoView({ block: 'nearest' })
      }
      // The sidebar row we're "on": the focused matter, else the DOM row for the
      // selected matter (recovers position after a focus-dropping re-render).
      const sidebarAnchor = focusedMatter
        || (typeof selectedMatterId === 'number'
            ? matterEls.find((el) => Number(el.dataset.matterId) === selectedMatterId) ?? null
            : null)
      const timelineAnchor = focusedCard
        || (typeof selectedArtifactId === 'number'
            ? cardEls.find((el) => Number(el.dataset.artifactId) === selectedArtifactId) ?? null
            : null)

      // ── Left: pane-2 → pane-1 (nearest matter by y) ──
      if (e.key === 'ArrowLeft') {
        if (!timelineAnchor || matterEls.length === 0) return
        const best = nearestByY(matterEls, timelineAnchor)
        const mid = Number(best.dataset.matterId)
        if (!Number.isFinite(mid)) return
        e.preventDefault()
        navZoneRef.current = 'sidebar'
        setSelectedMatterId(mid)
        go(best)
        return
      }
      // ── Right: pane-1 → pane-2 (nearest card by y) ──
      if (e.key === 'ArrowRight') {
        if (!sidebarAnchor || cardEls.length === 0) return
        const best = nearestByY(cardEls, sidebarAnchor)
        const aid = Number(best.dataset.artifactId)
        if (!Number.isFinite(aid)) return
        e.preventDefault()
        navZoneRef.current = 'timeline'
        setSelectedArtifactId(aid)
        go(best)
        return
      }

      // ── Up/Down: stay in the current zone ──
      const zone = focusedCard ? 'timeline' : focusedMatter ? 'sidebar' : navZoneRef.current
      const dir = e.key === 'ArrowDown' ? 1 : -1

      if (zone === 'sidebar') {
        if (matterEls.length === 0) return
        // Walk the VISIBLE matter rows in DOM order — this automatically jumps
        // OVER collapsed sub-folders (their matters aren't rendered) and goes
        // THROUGH expanded ones (their matters are). Skip consecutive rows of
        // the same matter (a matter filed in two folders renders twice).
        const curId = sidebarAnchor ? Number(sidebarAnchor.dataset.matterId) : NaN
        const idx = sidebarAnchor ? matterEls.indexOf(sidebarAnchor) : (dir > 0 ? -1 : matterEls.length)
        let next = idx + dir
        while (next >= 0 && next < matterEls.length && Number(matterEls[next].dataset.matterId) === curId) next += dir
        navZoneRef.current = 'sidebar'
        if (next < 0 || next >= matterEls.length) return  // at an edge — stay put
        e.preventDefault()
        const el = matterEls[next]
        const mid = Number(el.dataset.matterId)
        if (Number.isFinite(mid)) setSelectedMatterId(mid)
        go(el)
        return
      }

      // timeline zone — advance/retreat through pane-2 cards (state-anchored).
      if (cardEls.length === 0) return
      const ids = cardEls.map((el) => Number(el.dataset.artifactId))
      const cur = typeof selectedArtifactId === 'number' ? ids.indexOf(selectedArtifactId) : -1
      const nextT = cur < 0 ? (dir > 0 ? 0 : ids.length - 1)
        : (dir > 0 ? Math.min(ids.length - 1, cur + 1) : Math.max(0, cur - 1))
      if (nextT !== cur) {
        e.preventDefault()
        navZoneRef.current = 'timeline'
        setSelectedArtifactId(ids[nextT])
        go(cardEls[nextT])
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedArtifactId, selectedMatterId, setSelectedArtifactId, setSelectedMatterId])

  // Lift the keyboard-nav marker the moment the mouse moves again, so mouse
  // hover works normally outside of key browsing (Ken 2026-08-10).
  useEffect(() => {
    const onMove = () => document.documentElement.classList.remove('me-kbd-nav')
    window.addEventListener('mousemove', onMove)
    return () => {
      window.removeEventListener('mousemove', onMove)
      document.documentElement.classList.remove('me-kbd-nav')
    }
  }, [])

  // Auto-restore last-selected artifact on matter change — but only if
  // it's still in the matter's visible list. If the artifact was
  // re-routed to a different matter, marked ignored, or otherwise no
  // longer belongs here, leave selection empty so the third pane shows
  // its empty state instead of an orphan artifact.
  //
  // Wait until the list query has actually returned a result (loading
  // state shows []) before deciding membership — otherwise we'd skip
  // the restore on first render every single time.
  const matterListLoaded =
    typeof selectedMatterId === 'number'
      ? !matterArtifacts.isLoading && !!matterArtifacts.data
      : selectedMatterId === 'inbox'
        ? !inboxQuery.isLoading && !!inboxQuery.data
        : false
  // Auto-recall the last-viewed artifact for this matter. Decision logic
  // (when to fire, when to wait, when to skip) lives in decideRecallAction
  // in scrollMemory.ts and is unit-tested. This effect resolves inputs
  // and dispatches the result.
  const recallAttemptedFor = useRef<string | number | null>(null)
  useEffect(() => {
    const matterScope = selectedMatterId === 'inbox' ? 'inbox' : selectedMatterId
    const dataLoaded = selectedMatterId === 'inbox'
      ? !inboxQuery.isLoading
      : selectedMatterId !== null
        ? !matterArtifacts.isLoading
        : false
    const action = decideRecallAction({
      selectedMatterId,
      selectedArtifactId,
      matterListLoaded,
      artifactsLoaded: dataLoaded,
      storedArtifactId: matterScope ? recallLastArtifact(matterScope) : null,
      visibleArtifactIds: new Set(inboxOnly.map((a) => a.id)),
      recallAttemptedFor: recallAttemptedFor.current,
    })
    if (action.kind === 'wait') return
    // Mark BEFORE applying so a no-op outcome doesn't keep retrying. (The
    // recallAttemptedFor ref guards against background-refetch loops.)
    if (selectedMatterId !== null) recallAttemptedFor.current = selectedMatterId
    if (action.kind === 'select') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('artifact', String(action.artifactId))
        return next
      }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatterId, matterListLoaded, inboxOnly])

  const detail = useQuery({
    // Matter is part of the key so the same email opened in two matters
    // (e.g. m-032 vs m-033) doesn't serve a cached response scoped to the
    // wrong one — the backend narrows email_issue_stem / email_issue_summary
    // to ?matter=N, so each matter needs its own cache entry + re-fetch.
    queryKey: ['artifact', selectedArtifactId, selectedMatterId],
    queryFn: () => (selectedArtifactId
      ? artifacts.detail(
          selectedArtifactId,
          typeof selectedMatterId === 'number' ? { matter: selectedMatterId } : {},
        )
      : null),
    enabled: !!selectedArtifactId,
  })
  const detailResults = useQuery({
    queryKey: ['results', 'artifact', selectedArtifactId],
    queryFn: () => (selectedArtifactId ? resultsApi.list({ artifact: selectedArtifactId }) : Promise.resolve([])),
    enabled: !!selectedArtifactId,
  })
  // Routing assessments removed from the workspace right pane; they
  // live in Studio for prompt-iteration debugging.

  const queryClient = useQueryClient()

  // Hover-prefetch helper retained for future re-introduction of hover
  // prefetching on FolderSidebar matter rows (currently unused after the
  // flat list was replaced by the folder tree). Touch-noop'd via void
  // to satisfy noUnusedLocals.
  const prefetchMatterArtifacts = (matterId: number) => {
    queryClient.prefetchQuery({
      queryKey: ['artifacts', 'matter', matterId],
      queryFn: () => artifacts.list({ matter: matterId, limit: 500 }),
      staleTime: 30_000,
    }).catch(() => { /* best effort */ })
  }
  void prefetchMatterArtifacts

  const updateMatter = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Matter> }) =>
      mattersApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      // Folder counts (matter_count_self + matter_count_recursive) exclude
      // archived matters, so archive/restore changes the chip values.
      // Without this, the sidebar tree shows stale counts until the
      // 30s staleTime expires.
      queryClient.invalidateQueries({ queryKey: ['folders', 'tree'] })
    },
  })

  const filteredMatters = useMemo(() => {
    if (!matters.data) return []
    const base = !matterFilter.trim() ? matters.data : (() => {
      const q = matterFilter.toLowerCase()
      return matters.data.filter((m) => {
        // matter_keywords is the v2-renamed field — written to m.keywords
        // (mirrored from unranked_keywords for back-compat). Match at the
        // substring level so 'Joanne' hits a keyword of 'Joanne Smith'.
        const kw = (m.keywords || []).concat(m.unranked_keywords || [], m.ranked_keywords || [])
        return (
          m.name.toLowerCase().includes(q) ||
          m.matter_type.toLowerCase().includes(q) ||
          (m.composite_id || '').toLowerCase().includes(q) ||
          (m.summary || '').toLowerCase().includes(q) ||
          kw.some((k) => k.toLowerCase().includes(q))
        )
      })
    })()
    // Sort. Three modes:
    //   recent_desc: newest activity first (updated_at desc) — the proxy
    //     for "most recent activity" bumps on status change, summary
    //     update, pipeline writeback, etc.
    //   alpha:       A → Z by display name
    //   code:        M-### ascending (client_seq, creation order)
    const sorted = [...base]
    if (matterSort === 'alpha') {
      sorted.sort((a, b) => a.name.localeCompare(b.name))
    } else if (matterSort === 'code') {
      sorted.sort((a, b) => (a.client_seq || 0) - (b.client_seq || 0))
    } else {
      sorted.sort((a, b) => {
        const av = a.updated_at || a.created_at
        const bv = b.updated_at || b.created_at
        return bv.localeCompare(av)
      })
    }
    return sorted
  }, [matters.data, matterFilter, matterSort])

  // Proposed section deprecated; computed but unused. Kept for now in
  // case we revive the flow.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const proposedMatters = useMemo(
    // CIC demo matters are status='proposed' but nest in the normal tree (below);
    // keep them out of this standalone section so they don't double-render.
    () => filteredMatters.filter((m) => m.status === 'proposed' && !m.demo),
    [filteredMatters],
  )
  const openMatters = useMemo(
    () => filteredMatters.filter(
      (m) => m.status === 'intake' || m.status === 'active' || m.status === 'on_hold'
        // CIC demo matters (studio-only, status='proposed') nest in the normal
        // folder tree beside their live parent, flagged with a purple pip (Ken).
        || (m.status === 'proposed' && !!m.demo)
        // self_note matters are folder-assigned to the Peripheral "… — Self
        // Notes" sub-folders; include them here so FolderSidebar renders them
        // nested under those folders. They stay out of the headline count
        // (totalOpenMatters, computed separately) and, being folder-assigned,
        // never appear in the Unfiled section. Emerson 2026-08-11.
        || m.status === 'self_note',
    ),
    [filteredMatters],
  )
  const archivedMatters = useMemo(
    () => filteredMatters.filter((m) => m.status === 'closed'),
    [filteredMatters],
  )
  // Stable "N matters" total for the folder-tree header counter — active
  // matters, UNFILTERED by the search box (so the count doesn't jump around
  // while typing). Counts each matter once regardless of folder membership.
  const totalOpenMatters = useMemo(
    () => (matters.data ?? []).filter(
      (m) => m.status === 'intake' || m.status === 'active' || m.status === 'on_hold',
    ).length,
    [matters.data],
  )

  // Archived section open-state — auto-opens when a search filter
  // matches archived matters; restores the pre-search open state when
  // the filter clears.
  //
  // The pre-search snapshot is held in a ref (not state) so toggling
  // the archive while search is active doesn't re-snapshot, and so
  // toggling outside of search doesn't trigger the effect to overwrite
  // the snapshot.
  const [archiveOpen, setArchiveOpen] = useState(false)
  const archivePreSearchOpen = useRef<boolean | null>(null)
  // Ref on the <details> so we can scroll it into view when the user
  // explicitly opens the archived section. Without this the expanded
  // rows render below the visible scroll area.
  const archiveDetailsRef = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    const searching = !!matterFilter.trim()
    if (searching) {
      if (archivePreSearchOpen.current === null) {
        // Just started searching — capture the current state to
        // restore later. Subsequent search edits don't re-snapshot.
        archivePreSearchOpen.current = archiveOpen
      }
      if (archivedMatters.length > 0) setArchiveOpen(true)
    } else if (archivePreSearchOpen.current !== null) {
      // Search cleared — restore.
      setArchiveOpen(archivePreSearchOpen.current)
      archivePreSearchOpen.current = null
    }
    // archiveOpen intentionally not in deps: we only want to snapshot
    // at the moment search BECOMES active, not whenever the user
    // toggles the section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterFilter, archivedMatters.length])

  // Auto-open Archived when the selected matter is itself archived.
  // Otherwise hitting a deep-link / refresh / back-nav with an archived
  // matter selected lands on a workspace where the matter detail is
  // visible in column 2 but the row is hidden inside a closed Archived
  // section. The user can still close it manually after — we only
  // FORCE open, we don't fight subsequent toggles.
  useEffect(() => {
    if (typeof selectedMatterId !== 'number') return
    // Hard refresh stays fully collapsed: only force the Archived section open
    // for a closed matter once the user has interacted this session, not when
    // the selection is merely being restored from the URL on load (Ken 2026-08-19).
    if (!sidebarUserInteracted()) return
    const m = matters.data?.find((x) => x.id === selectedMatterId)
    if (m && m.status === 'closed') setArchiveOpen(true)
  }, [selectedMatterId, matters.data])

  // Clear the URL's `folder` param when it's stale — the selected
  // matter is no longer in that folder (dismissed via picker, folder
  // deleted, etc.). Without this, the FolderSidebar auto-expand effect
  // walks up from the stale selectedFolderId on every refresh and
  // re-opens folders the user explicitly closed. Symptom: close test2,
  // refresh, test2 reopens because URL still has ?folder=test2_id from
  // before you removed the matter from it.
  useEffect(() => {
    if (selectedFolderId == null) return
    if (typeof selectedMatterId !== 'number') return
    const m = matters.data?.find((x) => x.id === selectedMatterId)
    if (!m) return  // matter not loaded yet — don't clear, would lose context
    const inFolder = (m.folder_assignments || []).some((a) => a.folder === selectedFolderId)
    if (inFolder) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('folder')
      return next
    }, { replace: true })
  }, [selectedMatterId, selectedFolderId, matters.data, setSearchParams])


  const selectedMatter = typeof selectedMatterId === 'number'
    ? matters.data?.find((m) => m.id === selectedMatterId)
    : null
  // If the URL asks for a matter we don't have in cached data yet,
  // kick a refetch immediately rather than waiting for the next
  // background poll. Otherwise the spinner could sit for up to 60s
  // (the polling interval) — particularly noticeable when the user
  // navigates from /artifact/N back to /workspace?matter=N and the
  // cached matters list happens to be stale.
  useEffect(() => {
    if (typeof selectedMatterId === 'number' && !selectedMatter && !matters.isFetching) {
      matters.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatterId, selectedMatter])
  // Chat scope: matter when one is open, otherwise global. Selecting an
  // artifact inside a matter doesn't change the scope — the matter
  // conversation just learns which artifact you're looking at (passed
  // separately as "current view" context). Inbox / All artifacts both
  // share the same global thread.
  const chatScopeKind: 'matter' | 'global' =
    typeof selectedMatterId === 'number' ? 'matter' : 'global'
  const chatScopeId = chatScopeKind === 'matter' ? (selectedMatterId as number) : null
  const chatScopeLabel = chatScopeKind === 'matter'
    ? selectedMatter?.name ?? null
    : null

  // Clearing the search returns the WHOLE sidebar to a clean collapsed state,
  // whatever the current sort: empty the box, drop the matter/artifact/folder
  // selection atomically (folder too, so a later tree poll can't re-expand the
  // once-selected chain), collapse the Archived drawer (+ null the pre-search
  // snapshot so the restore-effect can't re-open it), signal the folder tree to
  // fully collapse, and refocus the box. Ken 2026-08-23. Shared by the MOVU
  // standalone clear button and the KEN in-field × (Ken 2026-10-23).
  const clearSearch = () => {
    setMatterFilter(''); traceSearch('')
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('matter', ''); next.delete('artifact'); next.delete('folder')
      return next
    }, { replace: false })
    setArchiveOpen(false); archivePreSearchOpen.current = null
    window.dispatchEvent(new Event('me-collapse-folders'))
    matterSearchRef.current?.focus()
  }

  // Left column widened from 320px → 400px (~25%) per Ken 2026-05-25
  // (yellow box markup). Middle pane is unchanged; remaining width
  // flows to pane 3.
  return (
    <div
      ref={gridRef}
      className={`relative grid h-full me-reading${playhead ? ' me-reading-split' : ''}`}
      style={{
        // Playhead (KEN): two explicit 7px divider tracks between the panes,
        // each a hover-only resize handle (transparent at rest). MOVU keeps the
        // 1px gap divider. (Ken 2026-10-22)
        gridTemplateColumns: playhead
          // Pane 1 = 346px (Ken 2026-10-23: widened +20% from 288 so matter names
          // read; this nudges pane-2 right). Pane-2 max widened 460→560 to take the
          // room pane-3 was wasting as a left gutter. 2.25rem collapsed rail
          // unchanged. KEN only; MOVU keeps 400px / minmax(380,460).
          // UXT-8 K3: pane-1 is a FIXED 346px at every reading size (reverts the
          // UXT-6 width-scaling — the folders never need to grow). K4: the pane-2/3
          // divider is a 32px WHITE gutter (no rule, no tint) so air separates the
          // matter name from the stem.
          ? `${sidebarCollapsed ? '2.25rem' : '346px'} 1px minmax(380px,560px) 32px 1fr`
          : `${sidebarCollapsed ? '2.25rem' : '400px'} minmax(380px,460px) 1fr`,
        gap: playhead ? '0' : 'var(--me-divider-w)',
        background: playhead ? 'transparent' : 'var(--me-divider-color)',
        ['--me-reading-scale' as string]: String(readingScale),
        // Inverse of the reading zoom — lets a pane-2/3 element cancel the zoom and
        // render at pane-1's (unscaled) size via .me-pane1-size (Ken 2026-10-25).
        ['--me-reading-inv' as string]: String(1 / readingScale),
      } as CSSProperties}
    >
      {/* Sidebar — clicks on the empty padding (not on a row) deselect
          everything and drop chat back to the global thread.
          setSelectedMatterId already deletes `artifact` from the URL,
          so we only call that one — calling both in the same handler
          races on react-router-v6's setSearchParams. */}
      <aside
        className="flex flex-col overflow-hidden bg-[color:var(--me-surface-1)]"
      >
        {sidebarCollapsed ? (
          <button
            type="button"
            onClick={() => setSidebarCollapsed(false)}
            title="Show folder bar"
            aria-label="Show folder bar"
            className="flex h-full w-full items-start justify-center pt-3 me-text-mute hover:me-text"
          >
            {playhead ? (
              /* GLY-0006 expand — non-square (32x24 frame), rendered 24x18, three
                 blocks (pane-1 back as a narrow pane), lens lower-right. KEN only. */
              <svg width={24} height={18} viewBox="0 0 32 24" fill="currentColor" aria-hidden="true" style={{ color: '#5C574E' }}>
                <path d="M2.0 3.6H6.6V20.4H2.0Z" />
                <path d="M9.0 3.6H17.4V20.4H9.0Z" />
                <path fillRule="evenodd" d="M19.8 3.6H30.0V20.4H19.8Z M26.0 13.8a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 1 0 0-4.8Z" />
              </svg>
            ) : (
              <PanelLeftOpen size={18} />
            )}
          </button>
        ) : (
        <>
        {/* Pinned header — search bar + sync line stay put while the folder
            tree scrolls beneath. The folder-controls row pins too, via sticky
            top-0 inside the scroll body below (Ken 2026-08-10). */}
        <div className="shrink-0 px-3 pt-3">
        {playhead && (
          /* KEN pane-1 head (SPEC rev 2026-10-25): SYNCED stamp first, then the FIND
             block — a ruled-line 168px search (no box) with the collapse glyph (GLY-0005)
             pinned to the pane's right edge, the gap between left empty. */
          <div className="mb-3">
            {/* SYNCED stamp (left) + collapse glyph in the pane's UPPER-RIGHT corner,
                mirroring the expand glyph's position on the collapsed rail (Ken 2026-10-25). */}
            <div className="flex items-start justify-between" style={{ marginBottom: 14 }}>
              <SyncedStamp />
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="shrink-0 flex items-center justify-center rounded-[5px] hover:bg-[color:var(--me-hover)]"
                // UXT-6 6A-2: non-text — centre on the matter-name cap-height (the
                // datum line), not its baseline. ~1.5px down from the pane top.
                style={{ width: 26, height: 26, color: '#776E5E', marginTop: 1.5 }}
                title="Hide folder bar — more room for chronologies & context"
                aria-label="Hide folder bar"
              >
                {/* UXT-5 5E: stock lucide PanelLeftClose (18px) to match the rest of
                    the chrome icons; replaced the bespoke GLY-0005 SVG. The EXPAND
                    control on the collapsed rail keeps its bespoke mark (5F). */}
                <PanelLeftClose size={18} />
              </button>
            </div>
            {/* UXT-1 (2026-11-03): KEN search field reverted to the v2 bordered
                input — a real me-input box, the "Search N matters (/)" placeholder,
                and the clear (×) inside the field at the right edge. The v3
                FIND-label-over-a-ruled-line treatment is retired. SYNCED (above)
                and WHAT'S NEW (the pane-1 register) are untouched. */}
            <div className="relative">
              <input
                ref={matterSearchRef}
                value={matterFilter}
                onChange={(e) => { setMatterFilter(e.target.value); traceSearch(e.target.value) }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ARTIFACT_CODE_RE.test(matterFilter.trim())) {
                    e.preventDefault()
                    void jumpToArtifactCode(matterFilter)
                  }
                }}
                placeholder={`Search ${totalOpenMatters.toLocaleString()} matters (/)`}
                className="me-input me-search-ken text-[12px] placeholder:text-[#CBC6BD] pr-7"
              />
              {ARTIFACT_CODE_RE.test(matterFilter.trim()) ? (
                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] me-mono me-text-mute">↵ open</span>
              ) : matterFilter.trim() !== '' ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center me-text-mute hover:me-text"
                  title="Clear search — Consult in global mode"
                  aria-label="Clear"
                ><Glyph name="search_clear" size={13}><X size={13} /></Glyph></button>
              ) : null}
            </div>
          </div>
        )}
        {!playhead && (
        <div className="mb-3 flex items-stretch gap-1.5">
          <div className="relative flex-1 min-w-0">
            {/* Placeholder names the active-matter count + client; the brand /
                nav colour outlines the box with a thin line (Ken 2026-08-10). */}
            <input
              ref={matterSearchRef}
              placeholder={`${playhead ? 'Find' : 'Search'} ${totalOpenMatters.toLocaleString()} matters (/)`}
              value={matterFilter}
              onChange={(e) => { setMatterFilter(e.target.value); traceSearch(e.target.value) }}
              onKeyDown={(e) => {
                // Enter on an EMAL/DOCU/ATCH code → jump to that artifact (see
                // jumpToArtifactCode). Non-code queries: Enter does nothing, as before.
                if (e.key === 'Enter' && ARTIFACT_CODE_RE.test(matterFilter.trim())) {
                  e.preventDefault()
                  void jumpToArtifactCode(matterFilter)
                }
              }}
              className={`me-input text-[12px] placeholder:text-[#CBC6BD]${playhead ? ' pr-7' : ''}`}
              style={{ borderColor: 'var(--me-nav-color)' }}
            />
            {/* Appears only when the box holds an artifact code — confirms the
                (otherwise unadvertised) Enter-to-jump is armed, stays invisible
                during normal matter-name typing. */}
            {ARTIFACT_CODE_RE.test(matterFilter.trim()) && (
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] me-mono me-text-mute">↵ open</span>
            )}
            {/* KEN: the clear (×) lives INSIDE the field at the right edge — a
                bare muted glyph, no border, shown only when there's text to
                clear (and not while the ↵-open hint is armed, so they never
                overlap). MOVU keeps the standalone bordered button below.
                (Ken 2026-10-23) */}
            {playhead && matterFilter.trim() !== '' && !ARTIFACT_CODE_RE.test(matterFilter.trim()) && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center me-text-mute hover:me-text"
                title="Clear search — Consult in global mode"
                aria-label="Clear"
              >
                <Glyph name="search_clear" size={13}><X size={13} /></Glyph>
              </button>
            )}
          </div>
          {/* CLEAR — clears the matter selection → global Consult (empties
              panes 2 & 3) and refocuses the search. Icon-only (X). MOVU only:
              on KEN this lives inside the search field (above). (Ken 2026-10-23) */}
          {!playhead && (
            <button
              onClick={clearSearch}
              className="me-btn me-btn-outline self-center !px-1.5 !py-1 !rounded-[5px] shrink-0"
              title="Clear matter selection + search box — Consult in global mode"
              aria-label="Clear"
            >
              <Glyph name="search_clear" size={13}><X size={13} /></Glyph>
            </button>
          )}
          {/* Collapse the folder bar to a thin rail, handing its width to the
              chronology + context panes (Maria 2026-10-18). KEN: bare glyph,
              hover-background only, no outline box (Ken 2026-10-23). */}
          <button
            onClick={() => setSidebarCollapsed(true)}
            className={playhead
              ? 'self-center flex items-center justify-center rounded-[5px] px-1.5 py-1 shrink-0 me-text-mute transition-colors hover:me-text hover:bg-[color:var(--me-hover)]'
              : 'me-btn me-btn-outline self-center !px-1.5 !py-1 !rounded-[5px] shrink-0'}
            title="Hide folder bar — more room for chronologies & context"
            aria-label="Hide folder bar"
          >
            <Glyph name="collapse_sidebar" size={13}><PanelLeftClose size={13} /></Glyph>
          </button>
        </div>
        )}
        {/* What's New — studio-only triage chat, tucked under the clear-X, no label
            (Ken 2026-10-12). Opens the shared agent-chat modal over recent activity.
            KEN: removed entirely — the matter chat now opens from the pane, so the
            sparkle has no job (Ken 2026-10-23). */}
        {canSeeWhatsNew && !playhead && (
          <div className="-mt-1.5 mb-2 flex justify-end">
            <button onClick={() => setWhatsNewOpen(true)}
              className="me-btn me-btn-outline self-center !px-1.5 !py-1 !rounded-[5px]"
              title="What's New" aria-label="What's New">
              <Glyph name="ai_sparkle" size={13}><Sparkles size={13} /></Glyph>
            </button>
          </div>
        )}
        {whatsNewOpen && (
          <AgentChatModal
            title="What's New"
            subtitle="Who emailed you and about what — last 2 days."
            suggestions={["What's new?", "What grew?", "Anything urgent?"]}
            ask={(messages) => artifacts.whatsNewChat(messages)}
            onClose={() => setWhatsNewOpen(false)}
          />
        )}

        {/* Sync status — replaces the old "In Process" counter (Ken 2026-08-10:
            that number was inscrutable). Wired to the corpus border (most recent
            email arrival), tenant-scoped, via LastSynced. (Ken 2026-08-17:
            removed the inbox-shortcut icon that used to sit to its right.) */}
        {/* KEN moves SYNCED into the pane-1 register (FolderSidebar); MOVU keeps
            the sync line here. */}
        {!playhead && (
        <div className="mb-3 px-1 flex items-center gap-1.5 text-[11px] me-text-mute me-chrome">
          <LastSynced />
        </div>
        )}
        {/* Proposed Agreements gutter — per-user opt-in (Heather/Maria). Renders
            nothing when the flag is off OR there are no in-flight agreements. */}
        {canSeeAgreementsGutter && <AgreementsGutter />}
        </div>
        {/* Scroll body — folder tree + archived. The folder-controls row is the
            first thing here and pins (sticky) to this container's top. */}
        <div
          ref={mattersListRef}
          className="me-scroll flex-1 min-h-0 overflow-auto px-3 pb-3"
          onClick={(e) => { if (e.target === e.currentTarget) setSelectedMatterId(null) }}
        >
        {/* Proposed section preserved-but-disabled 2026-05-07.
            History: pre-folders, new AI-created matters landed
            status='proposed' and waited for the user to Keep or Archive
            them via the Sparkles-marked section here. With folders
            shipped, the user's recourse for unwanted matters is to
            archive (or never visit them) — no need for a separate
            approval queue. The runner.py L3 step now defaults new
            matters to status='active'.
            To revive: flip `false` below to (e.g.) `client.ai_matter_default_status === 'proposed'`
            and change apps/pipeline/runner.py's `defaults={'status': 'active'}`
            back to 'proposed'. Could also become a per-client toggle —
            risk-averse firms might want the human-approval gate. */}
        {/* Proposed section — REVIVED 2026-10-01 for the CIC demo. Studio-only
            synthetic "proposed" re-dos (the API already hides demo matters from
            non-studio users). Purple = "about to be instantiated". */}
        {proposedMatters.length > 0 && (
          <>
            <div className="me-section-h mt-4 flex items-center gap-1" style={{ color: '#8b5cf6' }}>
              {/* purple sparkle removed per Ken 2026-10-14 */}
              <span>Proposed · {proposedMatters.length.toLocaleString()}</span>
            </div>
            {proposedMatters.map((m) => {
              const src = m.source_matter ? matters.data?.find((x) => x.id === m.source_matter) : null
              const srcLabel = src ? (src.composite_id || `M-${src.client_seq.toString().padStart(3, '0')}`) : ''
              return (
                <ProposedMatterRow
                  key={m.id}
                  matter={m}
                  sourceLabel={srcLabel}
                  selected={selectedMatterId === m.id}
                  onSelect={() => setSelectedMatterId(m.id)}
                  onKeep={() => updateMatter.mutate({ id: m.id, payload: { status: 'intake' } })}
                  onArchive={() => updateMatter.mutate({ id: m.id, payload: { status: 'closed' } })}
                  pending={updateMatter.isPending}
                />
              )
            })}
          </>
        )}

        {/* Folder tree — replaces the old flat "Matters · N" list. Matters
            appear nested under their folders (one occurrence per folder
            they're in); matters in zero folders show in the synthetic
            "Unfiled" section at the top of the tree. The flat sort
            controls (recent / oldest / alpha) only apply WITHIN a folder
            list; we leave them off the new sidebar for now. */}
        <div>
          <FolderSidebar
            matters={openMatters}
            selectedMatterId={selectedMatterId}
            selectedFolderId={selectedFolderId}
            onSelectMatter={setSelectedMatterInFolder}
            onArchiveMatter={(id) => updateMatter.mutate({ id, payload: { status: 'closed' } })}
            clientCode={authedClientCode}
            matterCount={totalOpenMatters}
            searchActive={!!matterFilter.trim()}
            onClearSearch={() => setMatterFilter('')}
            onEnterAffected={() => setArchiveOpen(false)}
            seenAtLoad={seenAtLoad}
            markSeen={markSeen}
          />
        </div>
        {filteredMatters.length === 0 && (
          <div className="px-2 pt-1 text-[12px] me-text-mute">no matches</div>
        )}

        {archivedMatters.length > 0 && (
          <details
            ref={archiveDetailsRef}
            className="mt-4"
            open={archiveOpen}
            onToggle={(e) => {
              const wasOpen = archiveOpen
              const isOpen = e.currentTarget.open
              setArchiveOpen(isOpen)
              // When the user explicitly opens the section, scroll its
              // header to the top of the sidebar — otherwise the
              // expanded rows often render below the viewport and the
              // user can't see what they just opened. Skip on close
              // (no point) and skip auto-opens triggered by a search
              // filter (those happen mid-typing; jumping the scroll
              // would feel rude). The 'wasOpen → isOpen' transition
              // catches the user-driven case specifically.
              if (!wasOpen && isOpen) {
                // Defer to next frame so the rows are in the DOM before
                // we measure scroll. Use the ref (not e.currentTarget)
                // — the synthetic event may be reused by then.
                requestAnimationFrame(() => {
                  archiveDetailsRef.current?.scrollIntoView({
                    behavior: 'smooth', block: 'start',
                  })
                })
              }
            }}
          >
            <summary className="me-section-h !mb-0 flex items-center gap-1 cursor-pointer hover:me-text">
              <Archive size={11} className="me-text-mute" />
              <span>Archived · {archivedMatters.length.toLocaleString()}</span>
            </summary>
            <div className="mt-1">
              {archivedMatters.map((m) => (
                <MatterRowCompact
                  key={m.id}
                  matter={m}
                  selected={selectedMatterId === m.id}
                  onSelect={() => setSelectedMatterId(m.id)}
                  onRestore={() => updateMatter.mutate({ id: m.id, payload: { status: 'intake' } })}
                  archived
                />
              ))}
            </div>
          </details>
        )}
        </div>
        </>
        )}
      </aside>

      {/* Pane-1 / pane-2 separator (Ken 2026-10-23 #1/#2): ONE 1px line in the
          nav-background token (the tan), no gutter — pane-2 starts immediately
          after. Replaces the old 7px hover-resize column (decorative; pane-1 is
          fixed width) and the aside's own border, which together read as a wide
          white gutter + a second line. */}
      {playhead && (
        <div className="h-full w-px" style={{ background: 'var(--me-nav-color)' }} />
      )}

      {/* Artifact list — when a matter is selected, the new
          MatterTimelinePane (matter header + Spine/Stages/Feed views)
          takes over. For inbox / no-selection, fall back to the
          existing flat list with bulk-toolbar.
          Loading guard: any time the URL says we want a matter
          (selectedMatterId is a number) but we don't yet have it in
          matters.data, show a spinner. The previous version only
          caught matters.isLoading — that's true on the FIRST fetch
          but false during background refetches when react-query is
          serving stale cached data. So a back-button into
          /workspace?matter=N&artifact=X could land momentarily on
          the inbox/home fallback (no header, empty list, but with
          the artifact still selected on the right) until matters
          refetched ~60s later. Now we keep showing the spinner until
          the matter actually appears in matters.data. The very rare
          edge case where a matter is genuinely missing (deleted /
          privacy-stripped) gets a stuck spinner, which is better
          than the inbox-flash UX. */}
      {typeof selectedMatterId === 'number' && !selectedMatter ? (
        <section className="me-scroll relative overflow-hidden bg-[color:var(--me-surface-2)]">
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-16"
            aria-label="Loading matter"
            role="status"
          >
            <div className="size-8 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
          </div>
        </section>
      ) : selectedMatter ? (
        <MatterTimelinePane
          matter={selectedMatter}
          artifacts={inboxOnly}
          selectedArtifactId={selectedArtifactId}
          onSelectArtifact={(id) => {
            // Re-click of the currently-selected attachment while in
            // markup mode exits markup; otherwise this is a normal
            // selection change (which also clears markup via
            // setSelectedArtifactId).
            if (id === selectedArtifactId && markupMode) {
              setMarkupMode(false)
              return
            }
            setSelectedArtifactId(id)
          }}
          markupArtifactId={markupMode ? selectedArtifactId : null}
          onOpenChangeCarry={(data, filename) => setChangeCarry({ data, filename })}
          onOpenMarkup={(id) => {
            // "markup" button → TOGGLE. If this doc is already open in
            // markup mode, turn it back OFF (show the doc again); else
            // select the doc and turn markup ON. Only gdoc linked-doc
            // cards call this (attachments use the non-clickable pip), so
            // the toggle is gdoc-only — docx/pdf behavior is untouched.
            // Emerson 2026-08-04.
            if (id === selectedArtifactId && markupMode) {
              setMarkupMode(false)
              return
            }
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev)
              next.set('artifact', String(id))
              next.set('markup', '1')
              return next
            }, { replace: false })
          }}
          scrollKey={`me.scroll.artifacts.${selectedMatter.id}`}
          // Use isPending (not isFetching) so the spinner only appears
          // when we genuinely have no data to show — on first load or
          // when switching matters. Background refetches (e.g. focus
          // refresh) where data already exists don't trigger the
          // overlay; they swap silently.
          isFetching={matterArtifacts.isPending && typeof selectedMatterId === 'number'}
          // No bulk-action toolbar in the matter view — bulk-run lives
          // in Studio. The matter view stays focused on reading.
        />
      ) : selectedMatterId === null ? (
        /* Clear / global-Consult state — pane-2 is intentionally empty
           (Emerson 2026-08-06); the global chat bubble is the interaction. */
        <section className="me-scroll overflow-auto bg-[color:var(--me-surface-2)]" />
      ) : (
      <section ref={inboxListRef} className="me-scroll overflow-auto bg-[color:var(--me-surface-2)] p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="me-section-h !mb-0">
            {selectedMatterId === 'inbox' && 'Inbox · unfiled'}
            {selectedMatterId === null && 'Home · no matter selected'}
          </div>
          <span className="me-mono text-[11px] me-text-mute">
            {selectedMatterId === 'inbox' && inboxTotal > inboxOnly.length
              ? `${inboxOnly.length.toLocaleString()} of ${inboxTotal.toLocaleString()}`
              : inboxOnly.length.toLocaleString()}
          </span>
        </div>

        {/* Bulk-action toolbar */}
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-sm border me-border bg-[color:var(--me-bg)] px-2 py-1.5 text-[11px]">
          <button
            onClick={() => {
              if (selectedIds.size === inboxOnly.length) setSelectedIds(new Set())
              else setSelectedIds(new Set(inboxOnly.map((a) => a.id)))
            }}
            className="me-btn me-btn-outline"
            title={selectedIds.size === inboxOnly.length ? 'Deselect all' : 'Select all visible'}
          >
            {selectedIds.size === inboxOnly.length && inboxOnly.length > 0 ? 'Deselect all' : 'Select all'}
          </button>
          <span className="me-mono me-text-mute">{selectedIds.size} selected</span>
          {isAdmin && (
            <button
              onClick={() => runBulk.mutate(Array.from(selectedIds))}
              disabled={selectedIds.size === 0 || runBulk.isPending}
              className="me-btn me-btn-primary"
            >
              <Play size={11} />
              {runBulk.isPending ? 'Enqueuing…' : 'Run on selected'}
            </button>
          )}
          {isAdmin && typeof selectedMatterId === 'number' && (
            <button
              onClick={() => runForMatter.mutate(selectedMatterId)}
              disabled={runForMatter.isPending}
              className="me-btn me-btn-outline ml-auto"
              title="Run pipeline against every artifact in this matter"
            >
              {runForMatter.isPending ? 'Enqueuing…' : 'Run pipeline on whole matter'}
            </button>
          )}
          {lastRunMsg && (
            <span className="me-chip me-chip-fresh">
              {lastRunMsg}
              <button onClick={() => setLastRunMsg(null)} className="ml-1" title="dismiss">
                <X size={9} />
              </button>
            </span>
          )}
        </div>

        {/* Top pagination — duplicated above the rows so the user
            doesn't have to scroll to flip pages. State is shared with
            the bottom pager (single inboxOffset), so they stay in sync
            for free. */}
        {selectedMatterId === 'inbox' && inboxTotal > 0 && (
          <div className="mb-2">
            <InboxPager
              offset={inboxOffset}
              pageSize={INBOX_PAGE_SIZE}
              total={inboxTotal}
              hasNext={!!inboxQuery.data?.next}
              onJump={inboxJump}
            />
          </div>
        )}
        <div
          className={`transition-opacity ${
            selectedMatterId === 'inbox' && inboxQuery.isFetching && inboxQuery.isPlaceholderData
              ? 'opacity-60'
              : ''
          }`}
        >
          {inboxOnly.map((a) => (
            <ArtifactRow
              key={a.id}
              artifact={a}
              selected={selectedArtifactId === a.id}
              picked={selectedIds.has(a.id)}
              onSelect={() => setSelectedArtifactId(a.id)}
              onTogglePick={() => {
                setSelectedIds((cur) => {
                  const next = new Set(cur)
                  if (next.has(a.id)) next.delete(a.id)
                  else next.add(a.id)
                  return next
                })
              }}
            />
          ))}
          {inboxOnly.length === 0 && <div className="px-2 pt-1 text-[12px] me-text-mute">No artifacts.</div>}
        </div>
        {/* Inbox prev/next pagination — matches the artifacts browser
            pattern. ±10 jumps are enabled when there's more than that
            many pages. */}
        {/* Bottom pagination — same shared state as the top one. */}
        {selectedMatterId === 'inbox' && inboxTotal > 0 && (
          <div className="mt-3">
            <InboxPager
              offset={inboxOffset}
              pageSize={INBOX_PAGE_SIZE}
              total={inboxTotal}
              hasNext={!!inboxQuery.data?.next}
              onJump={inboxJump}
            />
          </div>
        )}
      </section>
      )}

      {/* Pane divider 2 (playhead/KEN) — UXT-9 9A: a 32px white gutter with a 1px
          #e7e5e4 rule centred in it. The rule has a HOLE across the title band — it
          starts level with the top of the view-controls row (dividerRuleTop, measured)
          and runs unbroken to the bottom, so the matter name + stem share one open
          white band and the rest reads as two columns. */}
      {playhead && (
        <div className="relative h-full" style={{ background: 'var(--me-surface)' }}>
          <div className="absolute" style={{ top: dividerRuleTop, bottom: 0, left: '50%', width: 1, transform: 'translateX(-0.5px)', background: '#e7e5e4' }} />
        </div>
      )}

      {/* Detail */}
      <section className="me-scroll overflow-auto bg-[color:var(--me-surface-3)] p-5">
        {/* Measure cap (Maria 2026-10-19): hold the reading column to ~85
            characters and let the leftover width become margin, so larger
            Reading sizes don't produce unreadably long lines on a wide monitor.
            In ch units, so the cap tracks the (zoomed) type size. No-op when the
            pane is already narrower than 85ch. */}
        <div data-pane3-col className={`w-full max-w-[85ch] ${playhead ? '' : 'mx-auto'}`}>
        {detail.data ? (
          <ArtifactDetail
            artifact={detail.data}
            results={detailResults.data || []}
            allMatters={matters.data || []}
            isAdmin={isAdmin}
            // Ken 2026-07-21 — pass the currently-viewed matter so the
            // Re-file button knows the source. Null when the user is
            // browsing from the Inbox (nothing to subtract from).
            sourceMatterId={typeof selectedMatterId === 'number' ? selectedMatterId : null}
          />
        ) : selectedMatterId === null ? null : (
          /* Empty (no placeholder) in the Clear / global-Consult state;
             the "select an artifact" hint only shows within a matter/inbox. */
          <div className="text-[12px] me-text-mute">Select an artifact to view its details.</div>
        )}
        </div>
      </section>

      {/* Markup modal — opened via the "markup" button on a linked Google Doc
          card OR a docx attachment card. Both artifact types show their
          redlines/comments in this large centered overlay while the doc/preview
          stays in pane-3 underneath. Esc / ✕ / backdrop close via
          setMarkupMode. (PDFs never reach here — they carry no markup, so the
          button doesn't render.) */}
      {markupMode
        && (detail.data?.artifact_type === 'document' || detail.data?.artifact_type === 'attachment')
        && (() => {
        // Prefer the enriched attachment_markup arrays — they carry the
        // `characterization` type tag (extract_attachment_markup output) that
        // powers the Type column. Fall back to the raw comments/redlines when
        // the enrichment step hasn't run on this doc yet.
        const am = detail.data.attachment_markup
        const useR = !!(am?.redlines && am.redlines.length > 0)
        const useC = !!(am?.comments && am.comments.length > 0)
        return (
          <MarkupModal
            filename={detail.data.subject || ''}
            artifactId={detail.data.id}
            redlines={useR ? am!.redlines! : (detail.data.redlines || [])}
            comments={useC ? am!.comments! : (detail.data.comments || [])}
            onClose={() => setMarkupMode(false)}
          />
        )
      })()}

      {changeCarry && (
        <ChangeCarryModal
          data={changeCarry.data}
          filename={changeCarry.filename}
          onClose={() => setChangeCarry(null)}
        />
      )}

      {/* Chat — floating bubble bottom-right; click to expand. */}
      <ChatBubble
        scopeKind={chatScopeKind}
        scopeId={chatScopeId}
        scopeLabel={chatScopeLabel}
        currentArtifactId={selectedArtifactId}
      />
      {/* UXT-8 K7: second floating control (down-triangle) — opens an empty modal. */}
      <TriangleBubble />
    </div>
  )
}

function ProposedMatterRow({
  matter, selected, onSelect, onKeep, onArchive, pending, sourceLabel,
}: {
  matter: Matter
  selected: boolean
  onSelect: () => void
  onKeep: () => void
  onArchive: () => void
  pending: boolean
  sourceLabel?: string
}) {
  const PURPLE = '#8b5cf6'
  return (
    <div
      data-selected={selected ? 'true' : undefined}
      className="me-row mb-1 border-l-2"
      style={{ borderLeftColor: PURPLE }}
    >
      <button onClick={onSelect} className="block w-full text-left">
        <div className="flex items-center gap-1.5">
          {/* purple sparkle removed per Ken 2026-10-14 */}
          <span className="truncate font-medium me-text">{matter.name}</span>
        </div>
        <div className="me-mono mt-0.5 truncate text-[10px]" style={{ color: PURPLE }}>
          proposed{matter.demo ? ' · demo' : ''} · {matter.matter_type}
          {sourceLabel ? ` · re-do of ${sourceLabel}` : ''}
        </div>
        {matter.summary && (
          <div className="mt-0.5 line-clamp-2 text-[11px] me-text-dim">
            {matter.summary}
          </div>
        )}
      </button>
      {/* Same outlined style for both, hover lights up. Aligned right. */}
      <div className="mt-1.5 flex gap-1 justify-end">
        <button
          onClick={onKeep}
          disabled={pending}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5 hover:!bg-[color:var(--me-fresh-bg,var(--me-selected))] hover:!border-[color:var(--me-fresh,var(--me-fg))] hover:!text-[color:var(--me-text)]"
          title="Keep — open this matter for filing"
        >
          <Check size={10} /> Keep
        </button>
        <button
          onClick={onArchive}
          disabled={pending}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5 hover:!bg-[color:var(--me-stale-bg,var(--me-selected))] hover:!border-[color:var(--me-stale,var(--me-fg))] hover:!text-[color:var(--me-text)]"
          title="Archive — set aside; you can restore it later"
        >
          <Archive size={10} /> Archive
        </button>
      </div>
    </div>
  )
}

// MatterRow + ArchivedMatterRow (old flat-list rows) removed; the
// FolderSidebar's MatterRowCompact handles both active and archived
// rendering now (with the `archived` prop).

function ArtifactRow({
  artifact, selected, picked, onSelect, onTogglePick,
}: {
  artifact: Artifact
  selected: boolean
  picked: boolean
  onSelect: () => void
  onTogglePick: () => void
}) {
  const multiIssue = (artifact.issue_count ?? 0) > 1
  return (
    <div
      data-selected={selected ? 'true' : undefined}
      data-artifact-id={artifact.id}
      className="me-row mb-1 flex items-start gap-2"
    >
      <input
        type="checkbox"
        checked={picked}
        onChange={onTogglePick}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 shrink-0 cursor-pointer"
        title="Select for bulk run"
      />
      <button onClick={onSelect} className="block flex-1 min-w-0 w-full overflow-hidden text-left">
        <div className="flex items-center gap-2 min-w-0">
          <span className="me-chip uppercase shrink-0">{artifact.artifact_type}</span>
          {artifact.parent_artifact && <Paperclip size={11} className="me-text-mute shrink-0" />}
          <span className="truncate font-medium me-text flex-1 min-w-0">{artifact.subject || '(no subject)'}</span>
          {artifact.duplicate_count > 0 && (
            <span
              className="me-chip shrink-0 me-mono"
              title={`Same email also received in ${artifact.duplicate_count} other mailbox${artifact.duplicate_count > 1 ? 'es' : ''}`}
            >
              +{artifact.duplicate_count} dupe
            </span>
          )}
          {artifact.visibility === 'restricted' && (
            <span className="me-chip me-chip-stale shrink-0" title="Restricted visibility"><Lock size={10} /></span>
          )}
        </div>
        <div className="me-mono mt-0.5 truncate text-[11px] me-text-mute">
          {artifact.from_name || artifact.from_email || '—'}
          {artifact.source_date && ` · ${formatDateShort(artifact.source_date)} (${formatRelative(artifact.source_date)})`}
          {multiIssue
            ? <span className="me-text"> · {artifact.issue_count} issues</span>
            : (artifact.matter_ids.length > 0 && ` · ${artifact.matter_ids.length} matter${artifact.matter_ids.length > 1 ? 's' : ''}`)}
        </div>
        {multiIssue && artifact.issues && (
          <MultiIssueBlock issues={artifact.issues} mode="unscoped" />
        )}
      </button>
    </div>
  )
}

// Work Product (Heather 2026-08-05; Pull modal Ken 2026-08-11). A "pull" flag
// in the pane-3 header. Pressing it opens a centered modal:
//   - Text selected → the modal captures that passage.
//   - No selection  → the modal pulls the whole email.
// The modal collects multi-select TYPE tags (advice given / language proposed)
// + an optional note, then appends one entry to the email's WorkProduct flag.
// Multiple pulls from the same email are always allowed — the button never
// disables and never toggles to a "remove" state.
type WPAnchor = { exact: string; prefix: string; suffix: string }

// PULL_TYPES + the pull dialog itself now live in ../components/PullDialog,
// shared with the markup Pull (markup modal) so the two never drift.

function captureSelectionAnchor(): WPAnchor | null {
  const sel = window.getSelection()
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null
  const raw = sel.toString()
  const exact = raw.trim()
  if (!exact) return null
  const range = sel.getRangeAt(0)
  const container = range.commonAncestorContainer
  const el = (container.nodeType === 1 ? container : container.parentElement) as Element | null
  // Prefer the email body region (BodyPreview's data-wp-body) so prefix/
  // suffix are pure body text — not the pane-3 toolbar / header chrome.
  // Fall back to the article, then the element itself.
  const root = el?.closest('[data-wp-body]') || el?.closest('article') || el || null
  let prefix = ''
  let suffix = ''
  if (root) {
    try {
      const pre = range.cloneRange()
      pre.selectNodeContents(root)
      pre.setEnd(range.startContainer, range.startOffset)
      const start = pre.toString().length
      const full = root.textContent || ''
      prefix = full.slice(Math.max(0, start - 40), start)
      const end = start + raw.length
      suffix = full.slice(end, end + 40)
    } catch {
      // Range spanning detached nodes — keep the exact text, skip context.
    }
  }
  return { exact, prefix, suffix }
}

function WorkProductButton({ artifact, asMenuItem, onDone }: { artifact: ArtifactDetail; asMenuItem?: boolean; onDone?: () => void }) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  // open === null → closed. Otherwise carries the captured selection (or null
  // for a whole-email pull).
  const [open, setOpen] = useState<{ anchor: WPAnchor | null } | null>(null)
  const [types, setTypes] = useState<Set<WorkProductType>>(new Set())
  const [expanded, setExpanded] = useState(false)
  const pendingAnchor = useRef<WPAnchor | null>(null)

  function onMouseDown() {
    // Capture BEFORE the click steals focus and collapses the selection.
    pendingAnchor.current = captureSelectionAnchor()
  }

  function openModal() {
    const anchor = pendingAnchor.current
    pendingAnchor.current = null
    setTypes(new Set())
    setExpanded(false)
    setOpen({ anchor })
  }

  function close() { setOpen(null) }

  function toggleType(slug: WorkProductType) {
    setTypes((prev) => {
      const next = new Set(prev)
      next.has(slug) ? next.delete(slug) : next.add(slug)
      return next
    })
  }

  async function save(note: string) {
    if (!open || busy) return
    setBusy(true)
    try {
      const typeList = PULL_TYPES.map((t) => t.slug).filter((s) => types.has(s))
      const n = note.trim()
      const excerpt = open.anchor
        ? { ...open.anchor, types: typeList, note: n }
        : { exact: '', whole_email: true, types: typeList, note: n }
      await workProduct.mark(artifact.id, { excerpt })
      qc.invalidateQueries({ queryKey: ['work-product'] })
      close()
    } catch {
      // Leave the modal open so she can retry.
    } finally {
      setBusy(false)
    }
  }

  const anchor = open?.anchor ?? null

  return (
    <div className={asMenuItem ? 'contents' : 'shrink-0 self-center'}>
      {/* Trigger — Chrome font. Never disabled; a prior pull never changes it
          (multiple pulls per email are allowed). asMenuItem renders a plain text
          row for the pane-3 Manage menu (Ken 2026-10-22 B). */}
      <button
        onMouseDown={onMouseDown}
        onClick={() => { openModal(); onDone?.() }}
        className={asMenuItem
          ? 'block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]'
          : 'me-chrome text-[10px] uppercase tracking-[0.05em] font-semibold me-text-mute hover:me-text'}
        title="Pull this email (or a selected passage) as work product"
      >
        {asMenuItem ? 'Pull…' : 'pull'}
      </button>

      {open && (
        <PullDialog
          types={types}
          onToggleType={toggleType}
          onCancel={close}
          onSave={save}
          busy={busy}
        >
          {/* Passage preview (or "Whole email"). Passage ink darkened
              (Ken 2026-08-12) for readability. */}
          {anchor ? (
            <>
              <div className={`text-[12px] italic me-text ${expanded ? '' : 'line-clamp-3'}`}>
                {anchor.exact}
              </div>
              {anchor.exact.length > 160 && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-1 text-[11px] me-text-mute hover:me-text underline"
                >
                  {expanded ? 'less' : 'more'}
                </button>
              )}
            </>
          ) : (
            <div className="text-[12px] italic me-text-mute">Whole email</div>
          )}
        </PullDialog>
      )}
    </div>
  )
}

function ArtifactDetail({
  artifact,
  results,
  allMatters,
  isAdmin,
  sourceMatterId,
}: {
  artifact: ArtifactDetail
  results: Awaited<ReturnType<typeof resultsApi.list>>
  allMatters: Matter[]
  isAdmin: boolean
  /** The matter currently selected in pane-2, if any. Enables the
   *  Re-file button — null when viewing from the Inbox (nothing to
   *  subtract from). Ken 2026-07-21. */
  sourceMatterId: number | null
}) {
  const matterById = useMemo(() => new Map(allMatters.map((m) => [m.id, m])), [allMatters])
  const { unfiledLabel } = useProductName()  // per-tenant "Unfiled"/"Personal" label
  // thread-id / content-hash are operator/debug identifiers — keep them for
  // studio + superuser, but hide from client end-users (Heather/Maria) even
  // when Identifiers are on (Heather 2026-08-18).
  const auth = useAuth()
  const showIdentDebug = auth.status === 'authenticated' && auth.user.has_studio_access
  // Quiet pane-3 (Ken 2026-10-22): on KEN the thread id is provenance, not reading
  // material — hide the inline block and fold it into the artifact-ID hover.
  const detailPlayhead = auth.status === 'authenticated' && isPlayheadTenant(auth.user.client?.code)
  // The matter currently in context (pane-2). Its folder position + re-file
  // affordance replace the redundant matter-name chip below (Ken 2026-08-18).
  const currentMatter = sourceMatterId != null ? matterById.get(sourceMatterId) : undefined
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  // CIC hand-off memo (fixed subject) → show the "Reply to Submitter" concept
  // button beneath it in pane-3 (Ken 2026-10-01).
  // Keyed on the stable source id (cic-memo-… / cic-demo-memo-…), NOT the subject —
  // the subject is now a human label ("Agreement Submission Workspace") and renamable.
  const isCicMemo = /cic-(?:demo-)?memo-/.test(artifact.source_external_id || '')
  // The Approvals console (paralegal's pre-GC intake console) is SNAPSHOTTED onto the
  // memo artifact at promotion (the submission is ephemeral), so we read it straight
  // off the artifact — no fetch, survives submission clearing. Ken 2026-10-10.
  const cicApprovals = artifact.cic_approvals as unknown as ApprovalsData | undefined
  const [replyOpen, setReplyOpen] = useState(false)
  const [prioritiesOpen, setPrioritiesOpen] = useState(false)
  const aqGlyph = getAgreementQueueGlyph()
  // Promotion state ("Ready for Work"). Reflects the memo's cic_stage; toggled by the button.
  const [stage, setStage] = useState(artifact.cic_stage || 'intake')
  useEffect(() => { setStage(artifact.cic_stage || 'intake') }, [artifact.id, artifact.cic_stage])
  const promoteReady = async () => {
    try { setStage(await artifacts.promoteReady(artifact.id)) } catch { /* leave as-is */ }
  }
  // Submitter first name for the reply draft — the memo carries the submitter in
  // from_name ("Anderson, Jill" → "Jill"); undefined falls back to a placeholder.
  const submitterFirst = (() => {
    const n = (artifact.from_name || '').trim()
    if (!n) return undefined
    return n.includes(',') ? (n.split(',')[1].trim().split(/\s+/)[0] || undefined) : n.split(/\s+/)[0]
  })()
  // Memo stem metadata (Ken 2026-10-11): "Submitted By / <full name> · <when> / <folder>".
  // from_name is stored "Last, First" → show "First Last"; source_date is the submission
  // time; the folder comes off the matter in context (pane-2's folder assignment).
  const submitterName = (() => {
    const n = (artifact.from_name || '').trim()
    if (!n) return artifact.from_email || '—'
    if (n.includes(',')) {
      const [last, ...rest] = n.split(',')
      return `${rest.join(',').trim()} ${last.trim()}`.trim()
    }
    return n
  })()
  const submittedAt = artifact.source_date
    ? new Date(artifact.source_date).toLocaleString('en-US')
    : ''
  const memoFolderName = (currentMatter?.folder_assignments || [])
    .map((a) => a.folder_name)
    .filter(Boolean)
    .join(', ')
  // Classifications panel is admin-only and collapsed by default per
  // Ken 2026-05-25 (markup callout 12). Regular users never see it.
  const [classificationsOpen, setClassificationsOpen] = useState(false)
  // Gmail deep-link — emails only; opens the exact underlying message so the
  // user can reply without searching their inbox (Heather 2026-08-14).
  // Reply-in-Gmail — shown ONLY when the logged-in user is a PARTICIPANT of the
  // email (their address in from/to/cc). Otherwise the message isn't in their
  // mailbox, so the deep-link would just land on an empty search (Ken 2026-08-19).
  // And it pins to THEIR account (not the sync-source mailbox), so it never errors.
  const userEmail = auth.status === 'authenticated' ? (auth.user.email || '').toLowerCase() : ''
  const isParticipant = !!userEmail && [
    artifact.from_email || '',
    ...(artifact.to_emails || []),
    ...(artifact.cc_emails || []),
  ].some((s) => String(s).toLowerCase().includes(userEmail))
  const gmailUrl = (artifact.artifact_type === 'email' && isParticipant)
    ? gmailReplyUrl(artifact, userEmail)
    : null
  return (
    <article>
      {/* A contract memo IS the submission workspace — clean stem above the console,
          suppressing the techy subject + ATCH id header (Ken 2026-10-11). */}
      {isCicMemo && (
        <>
          <h2 className="me-summary text-[18px] font-medium me-text leading-snug mb-3">Submission Review Workspace</h2>
          {/* Metadata styled exactly like the "Sent By / Sent To" blocks on normal
              artifacts (ArtifactRecipients): me-idfont title-case semibold labels,
              me-connected values; the folder is a me-chip with a folder icon, like the
              header's folder-position chip. (Ken 2026-10-11) */}
          <div className="mb-5 space-y-2">
            <div>
              <div className="me-idfont mb-1 text-[10px] font-semibold me-text-mute">Proposed Agreement Submitted By</div>
              <div className="me-connected text-[12.5px] me-text">
                {submitterName}{submittedAt && ` · ${submittedAt}`}
              </div>
            </div>
            {memoFolderName && (
              <div>
                <span className="me-chip inline-flex items-center gap-1">
                  <Glyph name="subfolder" size={10}><FolderIcon size={10} /></Glyph>
                  {memoFolderName}
                </span>
              </div>
            )}
          </div>
          {(cicApprovals?.groups?.length ?? 0) > 0 && (
            <ApprovalsConsole
              data={cicApprovals}
              artifactId={artifact.id}
              agreementName={currentMatter?.name}
              comm={artifact.cic_comm}
            />
          )}
          {/* Workflow actions. "Ready for Work" (leftmost) promotes this agreement into the
              work queue; "Agreement Queue" opens the office-wide priorities chat. Glyph slots
              are custom-uploaded on QA; blank until set. (Ken 2026-10-12) */}
          <div className="mt-2 mb-6 flex flex-wrap items-center gap-2">
            <button onClick={promoteReady}
              title={stage === 'ready_for_work' ? 'Promoted — click to move back to intake' : 'Promote into the work queue'}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium inline-flex items-center gap-1.5"
              style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }}>
              {/* A checkbox: empty square until promoted, green ✓ once promoted (Ken 2026-10-12). */}
              {stage === 'ready_for_work'
                ? <span style={{ color: '#15803d', fontSize: 14, lineHeight: 1 }}>✓</span>
                : <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, border: '1.5px solid var(--me-border)' }} />}
              Ready for Work
            </button>
            <button onClick={() => setPrioritiesOpen(true)}
              className="rounded-md px-3 py-1.5 text-[13px] font-medium inline-flex items-center gap-1.5"
              style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }}>
              {aqGlyph
                ? <img src={aqGlyph} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                : <span style={{ display: 'inline-block', width: 16, height: 16 }} />}
              Agreement Queue
            </button>
          </div>
          {prioritiesOpen && <PrioritiesChat onClose={() => setPrioritiesOpen(false)} />}
        </>
      )}
      {/* Restricted-visibility badge is a real signal for the user — keep it.
          The EMAIL chip, source_type chip, and composite_id (system metadata,
          callout 11) are removed from the user surface per Ken 2026-05-25. */}
      {!isCicMemo && artifact.visibility === 'restricted' && (
        <div className="mb-2">
          <span className="me-chip me-chip-stale flex items-center gap-1 inline-flex"><Lock size={10} /> restricted</span>
        </div>
      )}
      {!isCicMemo && (
      <div className="flex items-start justify-between gap-2">
        {/* Artifact title — pencil-edit removed per Ken 2026-05-25 (callout 6).
            Composite id (e.g. MOVU-GOOG-EMAL-038606) restored as a muted
            bracketed suffix on the subject — Ken 2026-05-25 follow-up.
            EMAIL/gmail chips stay gone (callout 11). */}
        <h2 className="me-summary me-stem-title text-[18px] me-text leading-snug flex-1 min-w-0 break-words" style={{ fontWeight: 'var(--me-font-weight-gen-3)', marginTop: -3 }}>
          {(() => {
            // Ken 2026-07-07 memo — pane-3 header prefers the L6 stem
            // (attachment_stem / email_issue_stem) over the raw
            // filename / raw subject. Falls back to subject when the
            // stem hasn't been produced yet (pipeline still running,
            // or attachment class doesn't trigger Summarize).
            // artifact.subject stores the filename for attachments and
            // the subject line for emails, so the fallback is
            // artifact_type-agnostic.
            const stem = artifact.artifact_type === 'attachment'
              ? (artifact.attachment_stem || '')
              : (artifact.email_issue_stem || '')
            if (stem) return stem
            return artifact.subject || <span className="me-text-mute italic">(no subject)</span>
          })()}
          {artifact.composite_id && (
            /* me-identifier — hidden when Tweaks → IDENTIFIERS is OFF
               (Ken 2026-07-08 tweak #4). Pane-2's chronology detail
               brackets are unaffected — those are rendered by
               TimelineArtifactRow without this class, only when the
               chronology's "details" checkbox is on. */
            <span
              className={`me-identifier ml-2 align-middle ${detailPlayhead ? 'me-chrome-id tabular-nums' : 'text-[12px] font-normal me-text-mute'}`}
              title={detailPlayhead && artifact.gmail_thread_id
                ? `${artifact.composite_id}\nthread ${artifact.gmail_thread_id}`
                : (artifact.composite_id || '')}
            >
              {shortId(artifact.composite_id)}
            </span>
          )}
        </h2>
        {/* Re-file is the ONLY button on the pane-3 top line (Emerson
            2026-08-07). Pull + View moved down to the Content/Text/HTML/Copy
            toolbar line (passed to ArtifactBodyPanel as headerActions). Only
            shown when we know a source matter — from the Inbox there's nothing
            to subtract from. */}
        {/* Group Refile + Wall in ONE items-center row so they align to each
            other, not independently to the (taller) title — the independent
            self-center drifted apart under browser zoom (Ken 2026-08-19). */}
        {/* UXT-8 K8: cap-centre the ⋯ cluster on the stem's FIRST line (self-start
            + the stem's own -3 nudge), not the middle of a 2-line stem. */}
        <div className="flex items-center gap-2 self-start shrink-0" style={{ marginTop: -3 }}>
          {/* UXT-8 K5/K6: Refile moved INTO the ⋯ menu (no standalone button) — it's
              used rarely and shouldn't invite frequent use. Other tenants keep Wall. */}
          {detailPlayhead
            ? <Pane3Manage artifact={artifact} sourceMatterId={sourceMatterId} allMatters={allMatters} />
            : <WallButton artifact={artifact} />}
        </div>
      </div>
      )}
      {/* Ken 2026-07-18 — stable content fingerprints. composite_id
          resets on each wipe/replay (it uses client_seq); these
          don't. Rendered under the me-identifier gate so casual
          view isn't cluttered — flip Tweaks → IDENTIFIERS on to
          see them alongside the composite_id above.

          Emerson 2026-07-19 vertical-stripe fix — moved OUT of the
          header flex row. Was between <h2> and the View button; a
          long unbreakable message_id token (~70 chars, no spaces)
          hogged the flex row's width, forcing the h2's `min-w-0
          break-words` to collapse the title to ~1 char wide, which
          rendered the subject as a vertical stripe of characters.
          Now sits on its own row below the header with break-all
          so long msg-ids wrap horizontally instead of stealing
          space from the title.

          Emerson 2026-08-04 — swapped msg-id (RFC 5322 Message-ID,
          never used in practice) for gmail_thread_id. The thread id
          is the operationally useful identifier: it's what the
          post-decision thread-coherence check keys on, so seeing it
          here lets you eyeball which artifacts share a conversation
          (e.g. why EMAL-4125 should have joined its thread's matter). */}
      {showIdentDebug && !detailPlayhead && (artifact.content_hash || artifact.gmail_thread_id) && (
        <div className="me-identifier me-mono mt-1 text-[10.5px] me-text-mute space-y-0.5 break-all">
          {artifact.content_hash && (
            <div title="sha256 of normalized artifact content — stable across wipe/replay">
              hash <span className="me-text-dim">{artifact.content_hash.slice(0, 16)}…</span>
            </div>
          )}
          {artifact.gmail_thread_id && (
            <div title="Gmail thread id — shared by every message in the same email conversation; the key the thread-coherence check groups on">
              thread <span className="me-text-dim">{artifact.gmail_thread_id}</span>
            </div>
          )}
        </div>
      )}
      {!isCicMemo && <ArtifactRecipients artifact={artifact} />}

      {/* Classifications — admin-only, collapsed by default. Hidden
          entirely from non-admins per Ken 2026-05-25 (callout 12). */}
      {isAdmin && results.length > 0 && (
        <div className="mt-5">
          <button
            onClick={() => setClassificationsOpen((v) => !v)}
            className="me-section-h flex w-full items-center justify-between cursor-pointer hover:me-text"
            title={classificationsOpen ? 'Collapse classifications' : 'Expand classifications'}
          >
            <span>Classifications <span className="me-text-mute me-mono">({results.length})</span></span>
            <span className="me-text-mute text-[10px]">{classificationsOpen ? '▼' : '▶'}</span>
          </button>
          {classificationsOpen && (
            <div className="grid grid-cols-2 gap-1.5">
              {results.map((r) => (
                <ClassificationCard key={r.id} r={r} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Matter assignments — wait until the matters query has loaded
          before rendering names. Without this guard, the chips briefly
          show #<db_id> on reload (matterById is empty until matters
          resolves) and then flicker to the real name + confidence.
          allMatters being empty AND matter_assignments being non-empty
          means matters is still loading; show a subtle placeholder
          row so the section doesn't pop in suddenly. */}
      {!isCicMemo && (() => {
        // Other matters this artifact is ALSO filed in (not the pane-2 one).
        const otherAssignments = (artifact.matter_assignments || []).filter((am) => am.matter !== sourceMatterId)
        const loadingMatters = allMatters.length === 0 && (artifact.matter_assignments?.length ?? 0) > 0
        if (!currentMatter && otherAssignments.length === 0 && !loadingMatters) return null
        const fa = currentMatter?.folder_assignments || []
        const folderLabel = fa.length === 0
          ? unfiledLabel
          : fa[0].folder_name + (fa.length > 1 ? ` +${fa.length - 1}` : '')
        return (
          <div className="mt-2">
            <div className="flex flex-wrap gap-1.5">
              {loadingMatters ? (
                <span className="me-chip me-text-mute italic">loading…</span>
              ) : (
                <>
                  {/* Current matter → its FOLDER position. The matter NAME is
                      redundant (already atop pane-2), so we show where it's filed
                      instead, and clicking opens the same folder-filing modal as
                      pane-1's → button (Ken 2026-08-18). */}
                  {currentMatter && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFolderPickerOpen(true) }}
                      title={`Filed in: ${folderLabel} — click to move to folders`}
                      className="me-chip me-folder-loc inline-flex items-center hover:bg-[color:var(--me-hover)] uppercase text-[10px] tracking-[0.11em]"
                    >
                      {/* UXT-8 K10 (supersedes 8B): no glyph. The pill says "from a
                          pane-1 folder" by matching how pane-1 writes folder names —
                          uppercase Arial, .11em, weight 500 (.me-chip), #57534e
                          (.me-folder-loc→textDim) — at pill scale (10px vs pane-1's 11.5). */}
                      {folderLabel}
                    </button>
                  )}
                  {/* Other matters this artifact is ALSO filed in — kept (not
                      redundant): they tell you about cross-filing. */}
                  {otherAssignments.map((am, i) => {
                    const m = matterById.get(am.matter)
                    if (!m) return null
                    return (
                      <span key={i} className="me-brand me-chip">
                        {m.name}
                      </span>
                    )
                  })}
                </>
              )}
            </div>
          </div>
        )
      })()}
      {folderPickerOpen && currentMatter && (
        <FolderPickerModal
          matter={currentMatter}
          currentFolderIds={new Set((currentMatter.folder_assignments || []).map((a) => a.folder))}
          onClose={() => setFolderPickerOpen(false)}
        />
      )}

      {/* Ken 2026-07-08 memo — pane-3 Summary section removed. Per-issue
          email summary + per-attachment summary are redundant with the
          summaries already shown on the pane-2 chronology cards. */}

      {/* Rule between the folder chip and the body toolbar. KEN (Ken 2026-10-23
          #3): no rule — a 20px gap instead (folder chip → Text/HTML row = 20px;
          the 16px Text/HTML → first body line is set inside BodyPreview). MOVU
          keeps the rule. */}
      {!isCicMemo && (detailPlayhead
        ? <div style={{ height: 20 }} />
        : <div className="my-5 border-t me-border" />)}

      {/* Same previewer as the full ArtifactViewer page — image inline,
          PDF embed, .docx rendered via mammoth, HTML for emails, plain
          text, or a binary-fallback download. The Content header (label
          + mode toggle + download + copy) is rendered by BodyPreview.
          Clicking the "markup" badge opens the MarkupModal overlay; this
          panel keeps showing the preview underneath (the old in-pane
          MarkupTable swap is retired). */}
      {/* For a CIC memo the old checklist body is replaced by the console + Chat
          Transcript above — don't render the memo HTML (Ken 2026-10-11). */}
      {!isCicMemo && (
      <ArtifactBodyPanel
        artifact={artifact}
        centerActions={
          /* Reply-in-Gmail (emails only) + View sit in the middle, next to Copy.
             On KEN/playhead: View moves to the Manage menu and labels lose their
             all-caps (Ken 2026-10-22 B). */
          <>
            {gmailUrl && (
              <a
                href={gmailUrl}
                target="_blank"
                rel="noreferrer"
                className={`me-idfont self-center shrink-0 inline-flex items-center gap-1 me-text-mute hover:me-text ${detailPlayhead ? 'text-[11px]' : 'text-[10px] uppercase tracking-[0.05em] font-semibold'}`}
                title="Open this email in Gmail to reply to the sender"
              >
                <ExternalLink size={10} /> Reply in Gmail
              </a>
            )}
            {!detailPlayhead && (
              <Link
                to={`/artifact/${artifact.id}`}
                onClick={() => markScrollPendingRestore()}
                className="me-idfont self-center shrink-0 text-[10px] uppercase tracking-[0.05em] font-semibold me-text-mute hover:me-text"
                title="Open full viewer"
              >
                <Glyph name="view" size={12}>View</Glyph>
              </Link>
            )}
            {/* Remove — documents only (Ken 2026-08-19). Renders nothing for
                emails/attachments. */}
            <RemoveDocButton artifact={artifact} />
          </>
        }
        headerActions={
          /* Pull is right-justified — emails + documents (Ken 2026-08-19).
             On KEN/playhead it lives in the Manage menu instead. */
          detailPlayhead
            ? null
            : ((artifact.artifact_type === 'email' || artifact.artifact_type === 'document')
                ? <WorkProductButton artifact={artifact} />
                : null)
        }
      />
      )}

      {/* "Reply to Submitter" — superseded by per-unit Send Gmail; not shown for the
          memo workspace anymore (Ken 2026-10-11). */}
      {false && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setReplyOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-[12.5px] font-medium text-white"
            style={{ background: 'var(--me-accent, #2563eb)' }}
          >
            Reply to Submitter
          </button>
        </div>
      )}
      {replyOpen && (
        <ContractReplyModal
          submitterFirst={submitterFirst}
          agreement={currentMatter?.name}
          onClose={() => setReplyOpen(false)}
        />
      )}

      {/* Cross-mailbox dedup footer. When this artifact is the primary
          for a duplicate group, list which other mailboxes received the
          same email. When it IS a duplicate, point at the primary so
          the user can jump to it. */}
      {artifact.duplicates?.length > 0 && (
        <div className="mt-3 me-card p-3 text-[12px] me-text-dim">
          <div className="me-mono uppercase tracking-wider me-text-mute mb-1 text-[10.5px]">
            Also received in
          </div>
          <ul className="space-y-0.5">
            {artifact.duplicates.map((d) => (
              <li key={d.id} className="me-mono">
                {d.source_account_email || `(unknown mailbox) #${d.id}`}
              </li>
            ))}
          </ul>
        </div>
      )}
      {artifact.is_duplicate_of && (
        <div className="mt-3 me-card p-3 text-[12px] me-text-dim">
          This artifact is a cross-mailbox duplicate. The primary
          (with the AI analysis) is{' '}
          <Link
            to={`/artifact/${artifact.is_duplicate_of}`}
            className="underline"
          >
            artifact #{artifact.is_duplicate_of}
          </Link>
          .
        </div>
      )}

      {/* Markup below the body. On KEN/playhead (Ken T7 #2): nothing renders under
          the body — redlines are inline track changes, and the comment thread lives
          in pane-2 (the 2205/2206 Google-Docs notification emails) and the markup
          modal. Other tenants keep the original Comments/Redlines panels. */}
      {detailPlayhead ? null : (
        <>
          {artifact.comments?.length > 0 && (
            <div className="mt-5">
              <div className="me-section-h">Comments ({artifact.comments.length})</div>
              <ul className="space-y-1">
                {artifact.comments.slice(0, 10).map((c, i) => (
                  <li key={i} className="me-card p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="me-mono text-[11px] me-text-dim">{(c as any).author_name || (c as any).author_email}</span>
                      {(c as any).resolved && <span className="me-chip me-chip-fresh">resolved</span>}
                    </div>
                    <div className="mt-1 text-[12px] me-text">{(c as any).text}</div>
                    {(c as any).quoted && (
                      <div className="mt-1 me-mono text-[11px] me-text-mute italic">re: "{(c as any).quoted.slice(0, 120)}"</div>
                    )}
                  </li>
                ))}
                {artifact.comments.length > 10 && (
                  <li className="me-text-mute text-[11px]">+ {artifact.comments.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {artifact.redlines?.length > 0 && (
            <div className="mt-5">
              <div className="me-section-h">Redlines ({artifact.redlines.length})</div>
              <ul className="space-y-1">
                {artifact.redlines.slice(0, 8).map((r, i) => (
                  <li key={i} className="me-card p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="me-mono text-[11px] me-text-dim">{(r as any).author_name}</span>
                      <span className="me-chip">{(r as any).action}</span>
                    </div>
                    <div className="mt-1 me-mono text-[11px] me-text leading-snug">{(r as any).text}</div>
                    {(r as any).surrounding_context && (
                      <div className="mt-1 me-mono text-[10px] me-text-mute italic">"{(r as any).surrounding_context.slice(0, 100)}"</div>
                    )}
                  </li>
                ))}
                {artifact.redlines.length > 8 && (
                  <li className="me-text-mute text-[11px]">+ {artifact.redlines.length - 8} more</li>
                )}
              </ul>
            </div>
          )}
        </>
      )}
    </article>
  )
}

/** Content section in the right pane — header + toggle + download + copy
 *  are all rendered by BodyPreview; we only own the copy callback wired
 *  to the shared formatter.
 *
 *  When `markupMode` is on (clicked the green "markup" badge in pane-2),
 *  the content view is REPLACED — not overlaid — with the markup table.
 *  Same screen real estate, just different data. Exit via clicking the
 *  attachment card again (handled in Workspace's onSelectArtifact).
 */
function ArtifactBodyPanel({
  artifact, headerActions, centerActions,
}: { artifact: ArtifactDetail; headerActions?: React.ReactNode; centerActions?: React.ReactNode }) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    const ok = await copyArtifactToClipboard(artifact)
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  // Markup (redlines/comments) for BOTH attachments (docx) and linked Google
  // Docs now opens in the MarkupModal overlay (rendered at the Workspace root);
  // the preview stays rendered here underneath. So this panel always renders
  // BodyPreview — the old in-pane table swap is retired (Emerson 2026-08-05,
  // restored the attachment 'markup' button and unified both on the modal).
  //
  // A marked-up docx additionally suppresses BodyPreview's "Rendered" (mammoth)
  // toggle: mammoth renders the raw tracked-changes inline — the messy view we
  // retired for the modal. The clean extracted Text stays; markup lives in the
  // modal (Emerson 2026-08-07, re M-103's HH-edits docs).
  const hasMarkup = !!((artifact.redlines?.length) || (artifact.comments?.length))
  return (
    <BodyPreview
      mime={artifact.mime_type || ''}
      cachedContent={artifact.cached_content || ''}
      bodyHtml={artifact.body_html || ''}
      cachedFileUrl={artifact.cached_file_url || null}
      cachedFileSize={artifact.cached_file_size}
      storageMode={artifact.content_storage_mode || ''}
      sourceType={artifact.source_type}
      sourceExternalId={artifact.source_external_id}
      artifactType={artifact.artifact_type}
      pdfHeight="min(900px, 80vh)"
      onCopy={onCopy}
      copied={copied}
      suppressNativeToggle={hasMarkup}
      headerActions={headerActions}
      centerActions={centerActions}
    />
  )
}


function InboxPager({
  offset, pageSize, total, hasNext, onJump,
}: {
  offset: number
  pageSize: number
  total: number
  hasNext: boolean
  onJump: (deltaPages: number) => void
}) {
  const pageNum = Math.floor(offset / pageSize) + 1
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className="flex items-center justify-end gap-1.5 text-[10px]">
      <span className="me-mono me-text-mute mr-1">
        {`${(offset + 1).toLocaleString()}–${Math.min(offset + pageSize, total).toLocaleString()}`} of {total.toLocaleString()}
        {' · '}p {pageNum.toLocaleString()}/{lastPage.toLocaleString()}
      </span>
      <button
        disabled={offset === 0}
        onClick={() => onJump(-10)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
        title="Back 10 pages"
      >−10</button>
      <button
        disabled={offset === 0}
        onClick={() => onJump(-1)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
      >prev</button>
      <button
        disabled={!hasNext}
        onClick={() => onJump(1)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
      >next</button>
      <button
        disabled={pageNum >= lastPage}
        onClick={() => onJump(10)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
        title="Forward 10 pages"
      >+10</button>
    </div>
  )
}


function formatRelative(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const days = (now.getTime() - d.getTime()) / 86400000
  if (days < 1) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (days < 7) return `${Math.floor(days)}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function formatDateShort(iso: string): string {
  // "Jun 2, 2025 5:00pm" — concise, locale-neutral. Used in the
  // inbox row so we always see the actual date, not just relative.
  const d = new Date(iso)
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '')
  return `${date} ${time}`
}

declare global {
  interface Window {
    Awaited?: never
  }
}


// MatterBrief was the inline summary block above the artifact list.
// Replaced by the matter header in MatterTimelinePane (which mirrors
// the prototype). Keeping the file lean — if narrative_groups become
// useful in the new pane, port that block in.


// ─── Re-file popover (Ken 2026-07-21) ────────────────────────────
// Small button in pane-3 action row. Click → popover with target
// picker + optional "include N attachments" checkbox. Only shown when
// the user is viewing an artifact IN a matter — the source has to be
// unambiguous. Runs synchronously on the server (LLM side-effects
// bounded), so we invalidate queries + surface the outcome on return.

// Remove a document from the system — pane-3 middle toolbar, DOCUMENTS ONLY
// (Ken 2026-08-19). Soft-delete + recoverable; the original stays in Drive.
// Clears the artifact from the URL so pane-3 doesn't dangle on a removed doc.
function RemoveDocButton({ artifact }: { artifact: ArtifactDetail }) {
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const mut = useMutation({
    mutationFn: () => removeDocument(artifact.id),
    onSuccess: () => {
      qc.invalidateQueries()
      const next = new URLSearchParams(searchParams)
      next.delete('artifact')
      setSearchParams(next, { replace: true })
    },
  })
  if (artifact.artifact_type !== 'document') return null
  const go = () => {
    if (mut.isPending) return
    if (window.confirm(`Remove “${artifact.subject || 'this document'}”?\n\nIt’s removed here; the original stays in your Drive.`))
      mut.mutate()
  }
  // A <span>, not a <button> — matches View's <a> exactly and dodges Safari's
  // sticky button chrome/font (Ken 2026-08-19).
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
      title="Remove this document"
      className={`me-idfont self-center shrink-0 cursor-pointer text-[10px] uppercase tracking-[0.05em] font-semibold me-text-mute hover:me-text ${mut.isPending ? 'opacity-50 pointer-events-none' : ''}`}
    >
      {mut.isPending ? 'Removing…' : 'Remove'}
    </span>
  )
}


// Ethical wall — reactive affordance (Heather 2026-08-18). Owner-only, emails
// only. Clicking "Wall" opens a small two-option popover keyed on THIS email's
// sender: "From Matterlink" (banish — invisible to everyone) or "From other
// users" (restrict — others can't see it, she keeps access). Both create a
// SENDER wall from the email's from_email. A walled email shows a muted pill.
// Styled to match Re-file (same outline pill, self-center) so they sit level.
function WallButton({ artifact, asMenuItem, onDone }: { artifact: ArtifactDetail; asMenuItem?: boolean; onDone?: () => void }) {
  const auth = useAuth()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const canManage = auth.status === 'authenticated' && !!auth.user.can_manage_walls
  const isEmail = artifact.artifact_type === 'email'
  const walled = artifact.visibility !== 'shared'

  const mut = useMutation({
    mutationFn: (mode: 'hidden' | 'restricted') =>
      createWall({ artifactId: artifact.id, match: 'sender', mode }),
    onSuccess: (_data, mode) => {
      traceWallCreate({ match: 'sender', mode, source: 'pane3' })
      setOpen(false); onDone?.(); qc.invalidateQueries()
    },
  })

  if (!canManage || !isEmail) return null

  if (walled) {
    const label = artifact.visibility === 'hidden' ? 'Hidden' : 'Walled'
    if (asMenuItem) {
      return <div className="px-2 py-1 text-[12px] me-text-mute">{label}</div>
    }
    return (
      <span
        className="me-chrome me-btn me-btn-outline self-center me-text-mute"
        title="This sender is walled. Manage on the Walls page."
      >
        {label}
      </span>
    )
  }

  return (
    <div className={asMenuItem ? 'relative' : 'relative self-center flex'}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={mut.isPending}
        className={asMenuItem
          ? 'block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]'
          : 'me-chrome me-btn me-btn-outline'}
        title="Wall this sender"
      >
        {mut.isPending ? 'Walling…' : (asMenuItem ? 'Wall sender…' : 'Wall')}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-64 rounded border me-border p-2 shadow-lg"
             style={{ background: 'var(--me-bg, #fff)' }}>
          <div className="mb-1.5 px-1 text-[11px] me-text-mute">
            Wall <span className="me-text">{artifact.from_email || 'this sender'}</span>:
          </div>
          <button
            onClick={() => mut.mutate('hidden')}
            className="block w-full rounded px-2 py-1.5 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]">
            <div className="font-medium">From Matterlink</div>
            <div className="text-[11px] me-text-mute">Invisible to everyone, including you.</div>
          </button>
          <button
            onClick={() => mut.mutate('restricted')}
            className="mt-1 block w-full rounded px-2 py-1.5 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]">
            <div className="font-medium">From other users</div>
            <div className="text-[11px] me-text-mute">Others can’t see it; you keep access.</div>
          </button>
        </div>
      )}
    </div>
  )
}


// Pane-3 Manage (Ken 2026-10-22 B, KEN/playhead): one three-dot menu holding the
// secondary controls (View · Copy · Pull · Wall) so Refile is the only verb on the
// top line. Same pattern as the pane-1 matter-row Manage menu (outside-click +
// Escape + mouse-leave). Text-only items.
function Pane3Manage({ artifact, sourceMatterId, allMatters }: { artifact: ArtifactDetail; sourceMatterId: number | null; allMatters: Matter[] }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])
  const doCopy = async () => {
    const ok = await copyArtifactToClipboard(artifact)
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 2000) }
  }
  const canPull = artifact.artifact_type === 'email' || artifact.artifact_type === 'document'
  // UXT-8 K5/K6: no onMouseLeave-close — the Refile picker (a menu item) opens a
  // wider popover past the menu bounds; mouse-leave would kill it. Outside-click
  // + Escape still dismiss.
  return (
    <div className="relative self-center" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-open={open ? 'true' : undefined}
        className="inline-flex items-center justify-center rounded me-text-mute hover:me-text"
        style={{ width: 26, height: 26 }}
        title="Manage this item"
        aria-label="Manage this item"
      >
        <Glyph name="manage" size={15}><MoreHorizontal size={15} /></Glyph>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-44 rounded border me-border p-1 shadow-lg"
             style={{ background: 'var(--me-bg, #fff)' }} onClick={(e) => e.stopPropagation()}>
          <Link
            to={`/artifact/${artifact.id}`}
            onClick={() => { markScrollPendingRestore(); setOpen(false) }}
            className="block rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
          >View</Link>
          <button
            onClick={() => { doCopy(); setOpen(false) }}
            className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
          >{copied ? 'Copied' : 'Copy'}</button>
          {canPull && <WorkProductButton artifact={artifact} asMenuItem onDone={() => setOpen(false)} />}
          <WallButton artifact={artifact} asMenuItem onDone={() => setOpen(false)} />
          {/* UXT-8 K5/K6: Refile… as a menu item (was the standalone top-line button). */}
          {sourceMatterId != null && (
            <RefileButton artifact={artifact} sourceMatterId={sourceMatterId} allMatters={allMatters} asMenuItem onDone={() => setOpen(false)} />
          )}
        </div>
      )}
    </div>
  )
}


function RefileButton({
  artifact, sourceMatterId, allMatters, asMenuItem, onDone,
}: {
  artifact: ArtifactDetail
  sourceMatterId: number
  allMatters: Matter[]
  // UXT-8 K5/K6: render as a ⋯-menu row ("Refile…") instead of a standalone button.
  asMenuItem?: boolean
  onDone?: () => void
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [targetId, setTargetId] = useState<number | 'new' | null>(null)
  const [includeAttachments, setIncludeAttachments] = useState(true)
  const [filter, setFilter] = useState('')
  const [siblings, setSiblings] = useState<RefileSibling[]>([])
  const [checked, setChecked] = useState<Set<number>>(new Set())

  const isEmail = artifact.artifact_type === 'email'
  // children_count is {total, visible} on the API shape; total is the
  // real DB count (visible respects any filter). Use total here — the
  // re-file moves attachments regardless of what the pane-2 filter
  // hid.
  const childCount = artifact.children_count?.total ?? 0
  // Only show the attachments checkbox when there's something to
  // decide — a standalone attachment or an email with zero children
  // doesn't need the option.
  const showAttachmentsToggle = isEmail && childCount > 0

  const targetOptions = useMemo(() => {
    const active = allMatters.filter((m) => m.id !== sourceMatterId && m.status !== 'closed')
    const q = filter.trim().toLowerCase()
    const filtered = q
      ? active.filter((m) =>
          m.name.toLowerCase().includes(q)
          || (m.composite_id || '').toLowerCase().includes(q))
      : active
    return filtered.slice(0, 200).sort((a, b) => a.name.localeCompare(b.name))
  }, [allMatters, sourceMatterId, filter])

  // On open: reset the picker (nothing pre-selected — Ken 2026-08-18) and, for
  // an email, fetch its same-thread siblings for the "move these too" checklist.
  useEffect(() => {
    if (!open) return
    setTargetId(null)
    setFilter('')
    setChecked(new Set())
    if (!isEmail) { setSiblings([]); return }
    let cancelled = false
    refileSiblings(artifact.id)
      .then((r) => {
        if (cancelled) return
        // Only offer thread-mates that live in the SAME matter we're refiling
        // FROM. The backend returns cross-matter thread siblings by design, but
        // offering artifacts from OTHER matters is confusing and risks pulling
        // mail out of matters the user isn't even looking at (Emerson 2026-09-27).
        setSiblings(r.siblings.filter((s) => s.matter?.id === sourceMatterId))
      })
      .catch(() => { if (!cancelled) setSiblings([]) })
    return () => { cancelled = true }
  }, [open, isEmail, artifact.id, sourceMatterId])

  const refile = useMutation({
    mutationFn: () => refileArtifact({
      artifact_id: artifact.id,
      source_matter_id: sourceMatterId,
      target_matter_id: targetId === 'new' ? null : (targetId as number),
      include_attachments: showAttachmentsToggle ? includeAttachments : false,
      also_artifact_ids: Array.from(checked),
    }),
    onSuccess: (data: RefileResult) => {
      // Record the correction as an event on the Search page (end-users only;
      // gated inside traceRefile). Ken 2026-10-19.
      traceRefile({
        artifact_id: artifact.id,
        from_matter_id: sourceMatterId ?? null,
        to_matter_id: data.target_matter_id,
        to_matter_was_new: data.target_is_new,
      })
      // Invalidate everything that could show artifact→matter linkage.
      qc.invalidateQueries({ queryKey: ['artifacts'] })
      qc.invalidateQueries({ queryKey: ['artifact', artifact.id] })
      qc.invalidateQueries({ queryKey: ['matters'] })
      qc.invalidateQueries({ queryKey: ['matter', sourceMatterId] })
      if (data.target_matter_id) {
        qc.invalidateQueries({ queryKey: ['matter', data.target_matter_id] })
      }
      // Give the user a beat to read the result before dismissing.
      setTimeout(() => { setOpen(false); onDone?.() }, 1500)
    },
  })

  return (
    <div className={asMenuItem ? 'relative' : 'relative shrink-0'}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={asMenuItem
          ? 'block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]'
          : 'me-chrome me-refile me-btn me-btn-outline self-center'}
        title="Refile this artifact"
      >
        {asMenuItem ? 'Refile…' : 'Refile'}
      </button>
      {open && (
        <div
          className="me-chrome absolute right-0 top-full z-50 mt-1 w-[380px] me-card p-3 shadow-lg"
          style={{ background: 'var(--me-surface)' }}
        >
          <div className="mb-2 flex items-center justify-end">
            <button
              onClick={() => setOpen(false)}
              className="me-text-mute hover:me-text"
              title="Close"
            >
              <X size={12} />
            </button>
          </div>

          {/* Target picker */}
          <div className="mb-4">
            <div className="me-section-h mb-1">Move to</div>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="search"
              className="me-input !text-[11.5px] w-full mb-1"
            />
            <div className="max-h-52 overflow-y-auto overflow-x-hidden rounded border me-border">
              <button
                onClick={() => setTargetId('new')}
                data-selected={targetId === 'new' ? 'true' : undefined}
                className="me-row w-full px-2 py-1.5 text-left flex items-center gap-2"
              >
                <span className="text-[12px] font-medium">Create a new Matter with the selected email</span>
              </button>
              {targetOptions.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setTargetId(m.id)}
                  data-selected={targetId === m.id ? 'true' : undefined}
                  className="me-row w-full px-2 py-1.5 text-left items-center gap-2"
                  style={{ display: 'flex' }}  // .me-row sets display:block and wins over `flex`
                >
                  <span
                    className="me-data text-[10px] me-text-mute shrink-0"
                    style={{ width: '3.25rem', minWidth: '3.25rem' }}
                  >
                    M-{String(m.client_seq).padStart(3, '0')}
                  </span>
                  <span className="me-summary text-[11.5px] me-text truncate flex-1 min-w-0">{m.name}</span>
                </button>
              ))}
              {targetOptions.length === 0 && filter && (
                <div className="p-2 text-[11px] me-text-mute italic">
                  No matters match "{filter}".
                </div>
              )}
            </div>
          </div>

          {/* "Move these too" — same-thread siblings, user-selected. No
              assumptions: nothing pre-checked; she decides which follow. */}
          {siblings.length > 0 && (
            <div className="mb-2">
              <div className="me-section-h mb-1 flex items-center justify-between">
                <span>Move these too?</span>
                <button
                  onClick={() => setChecked(
                    checked.size === siblings.length
                      ? new Set()
                      : new Set(siblings.map((s) => s.artifact_id)))}
                  className="text-[10px] me-text-mute hover:me-text normal-case"
                >
                  {checked.size === siblings.length ? 'clear' : 'select all'}
                </button>
              </div>
              <div className="mb-1 text-[10.5px] me-text-mute">
                Check any that should follow the email you are refiling.
              </div>
              <div className="max-h-40 overflow-y-auto overflow-x-hidden rounded border me-border">
                {siblings.map((s) => (
                  <label
                    key={s.artifact_id}
                    className="me-row items-start gap-2 px-2 py-1.5 cursor-pointer"
                    style={{ display: 'flex' }}  // .me-row's display:block would kill the flex layout
                  >
                    <input
                      type="checkbox"
                      checked={checked.has(s.artifact_id)}
                      onChange={(e) => {
                        const next = new Set(checked)
                        if (e.target.checked) next.add(s.artifact_id)
                        else next.delete(s.artifact_id)
                        setChecked(next)
                      }}
                      className="mt-0.5 shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="me-text text-[11.5px] truncate block">{s.subject || '(no subject)'}</span>
                      <span className="me-text-mute text-[10px]">
                        {s.matter
                          ? `M-${String(s.matter.client_seq).padStart(3, '0')} ${s.matter.name}`
                          : 'unfiled'}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Attachments checkbox — emails-with-children only */}
          {showAttachmentsToggle && (
            <label className="mb-2 flex items-center gap-2 text-[11.5px] me-text cursor-pointer">
              <input
                type="checkbox"
                checked={includeAttachments}
                onChange={(e) => setIncludeAttachments(e.target.checked)}
              />
              <Paperclip size={10} />
              Include {childCount} attachment{childCount === 1 ? '' : 's'}
            </label>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setOpen(false)}
              className="me-btn me-btn-outline !text-[11px]"
              disabled={refile.isPending}
            >
              Cancel
            </button>
            <button
              onClick={() => { if (targetId !== null) refile.mutate() }}
              disabled={refile.isPending || targetId === null}
              className="me-btn !text-[11px] disabled:opacity-40"
              style={{ background: '#ea580c', color: '#ffffff', borderColor: '#ea580c' }}
              title={targetId === null ? 'Pick a destination first' : undefined}
            >
              {refile.isPending ? 'Refiling…' : 'Refile'}
            </button>
          </div>

          {refile.isError && (
            <div className="mt-2 text-[11px] text-[color:var(--me-urgent)]">
              {(refile.error as any)?.response?.data?.detail || (refile.error as Error).message}
            </div>
          )}
          {refile.isSuccess && refile.data && (
            <div className="mt-2 text-[11px] me-text-mute">
              <span className="text-[color:var(--me-fresh)]">✓ Refiled.</span>{' '}
              Moved {refile.data.moved_issue_ids.length} issue(s)
              {refile.data.moved_attachment_ids.length > 0 && (
                <> + {refile.data.moved_attachment_ids.length} attachment(s)</>
              )}
              {refile.data.target_is_new && refile.data.target_matter_id != null && (
                <> to new M-
                {String(allMatters.find((m) => m.id === refile.data!.target_matter_id)?.client_seq
                  ?? '?').padStart(3, '0')}</>
              )}.
              {(refile.data.also_moved ?? 0) > 0 && (
                <> {' '}+ {refile.data.also_moved} more thread email{refile.data.also_moved === 1 ? '' : 's'}.</>
              )}
              {(refile.data.archived_matter_ids?.length ?? 0) > 0 && (
                <> {' '}Archived {refile.data.archived_matter_ids!.length} empty matter{refile.data.archived_matter_ids!.length === 1 ? '' : 's'}.</>
              )}
              {/* Emerson 2026-07-21 — followup chain runs on celery
                  in the background. Description / name / summary /
                  phase groupings appear over the next ~30-90s as the
                  LLM calls complete; UI refetches will pick them up. */}
              <div className="mt-0.5">
                <span className="me-text-dim">
                  Description, name, summary refreshing in the background.
                </span>
              </div>
              {refile.data.followup_enqueue_error && (
                <div className="mt-0.5 text-[color:var(--me-stale)]">
                  Followup enqueue error: {refile.data.followup_enqueue_error}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
